import * as THREE from 'three';
import { chamberLayout, hexToWorld, mouthsLayout, sapFlow, type BattleState, type ChamberLayout, type DeepSapNode, type MapDef, type MouthsLayout, type RootsState } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { lookPoint, type OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * Inside the Grandsire (docs/sendas/druida.md, chapter 5), for the druid's path. Outside, at its foot, the wood at dusk
 * or at dawn; inside, living wood with its grain, the amber light that comes through the bark, sap that glows. The
 * board's tiles are hidden and each place lays its own floor. Each place:
 *
 * - `grandsireFoot` (the cinematic, the opening, the healed epilogue `breath`): the Grandsire close at its foot for the
 *   first time, a trunk as wide as a square to the north, its roots humped over the ground, the bark breathing (its cleft
 *   opening and shutting slowly, amber inside); the wood round it and the path down to the south where the colliers'
 *   smoke comes up. With `breath` (healed) the amber turns green. `grandsireFallen` (the released epilogue): the
 *   Grandsire lying over the wood, its stump torn, shafts of light coming in; a shoot a hand high in its ring of stones.
 * - `sapGallery` (act 2, the buds below): a gallery hollowed in the wood, walls of dark living wood with their grain and
 *   amber veins, a floor of heartwood cut in rings like a log; the sap's grooves from the top (the foot) down to each
 *   fork, each mouth and each side gallery that drinks; amber beads down them each pulse (bigger the more each carries,
 *   stopping at a shut knot); the knots (shut: a thick black band clenched across the groove; open: undone, pale, lying
 *   aside); the mouths stopped with rot (a swollen violet-black plug, its notches on a dark slab: what it asks), open
 *   (a ring of fresh wood, green and amber light), cracked (amber running); under each open mouth its net: the stones,
 *   the two sound roots growing node to node (a tangle in red), the moss sprouted. The drip: a red ring closing.
 * - `sapHollow` (the campfire): a round hollow of the trunk, its walls glowing amber.
 * - `carcomaChamber` (act 4; the close with `face`), `carcomaMouth` (the knife, the speech): the round chamber at the
 *   bottom of all, where the wood touches the titan's flesh (the north wall smooth, dark red, beating slowly); black earth
 *   between roots; the Carcoma's four roots to the walls (mud: thick, wet, dark; resin: dry amber, its smoke bending the
 *   air's way along its patches; hidden: in sight where it goes in and comes out, mushrooms over the floor between,
 *   black over the rot, white over sound wood; frost: blue ice, its three buds and their little crown off a spring in the
 *   wall); a root cut goes grey and dry with a flash; the mud's net, the resin's patches (alight, burnt), Elvira's three
 *   knots of sound root by the way to the door and the gate they shut; the door of roots at the foot with the colliers'
 *   lanterns. Warnings on the floor, 3 s: the splinters' cone, the bite, the swipe at the seed, the black thread on the
 *   wall where a root grows back; the trail of rot; the rot coming in from the walls (outside the safe circle, dark).
 *   With `face`: Olmo's last face on the flesh wall, eyes and no mouth; `carve` cuts its mouth and the line under it.
 *
 * Draw calls: the dressing (one merged mesh), its veins (one, lit), the flesh (one, beating), the rings (one
 * instanced), the beads (one), the knot bands (one), the plugs and the open mouths (two), the stones' sprouts (one),
 * the net roots (one), the chamber's roots (one), the resin patches (one), the mushrooms (two), the gate bars (one),
 * the trail (one), the cone (one), the rot ring (one), the glow layer, the sky dome outside. No lights of its own.
 */

export const RAICES_PLACES = new Set(['grandsireFoot', 'grandsireFallen', 'sapGallery', 'sapHollow', 'carcomaMouth', 'carcomaChamber']);

const S = WORLD_PER_HEX;
const K = S / 1000;
/** A hex's circumradius in world units (pointy-top, as the sim lays them out). */
const HEXR = 0.5774 * S;

const WOOD = ['#3a2416', '#44291a', '#4e3020', '#35210f', '#563622'];
// (A warm mid-brown, never pale: under the amber key a light heartwood turns cream on a phone and the sap's beads are lost on it.)
const HEART = ['#7a5232', '#84593a', '#6e4a2c', '#8a603e'];
const EARTH = ['#2a201c', '#32261f', '#241c18', '#2e231d'];
// (A dark cold red: a warmer, lighter one turned a raw bright red under the amber key.)
const FLESH = ['#42202a', '#4a2430', '#3a1c26', '#502a34'];
const BARK = ['#3e2c22', '#4a362a', '#34261e', '#56402e'];
const MOSS = ['#3e5a2a', '#4a6a30', '#36522a', '#56743a'];
const AMBER = new THREE.Color('#ffae3c');
const AMBER2 = new THREE.Color('#ff8a2a');
const GREEN = new THREE.Color('#9ee070');
const WARN = new THREE.Color('#ff4f3a');
const WARN2 = new THREE.Color('#ff8a6e');
const WHITE = new THREE.Color('#eef2f6');
const BLACK = new THREE.Color('#141016');
const ROT = new THREE.Color('#2e1e34');
const SOUND = new THREE.Color('#7a8a3c');
const TANGLE = new THREE.Color('#c4402a');
const DRY = new THREE.Color('#5e5852');
const ICE = new THREE.Color('#8ec8f0');
const EMBER = new THREE.Color('#ff7a2a');
/** The glow running along each of the Carcoma's roots while it feeds her: mud, resin, hidden, frost. */
const FEED = [new THREE.Color('#7a5a40'), new THREE.Color('#ffb040'), new THREE.Color('#c890ff'), new THREE.Color('#bfe8ff')];

/** Part of a material's own (instance) colour added to its light: it keeps its colour under the place's amber. */
const selfLit = (m: THREE.MeshStandardMaterial, k: number): THREE.MeshStandardMaterial => {
  m.onBeforeCompile = (sh) => {
    sh.fragmentShader = sh.fragmentShader.replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\n\ttotalEmissiveRadiance += diffuseColor.rgb * ${k.toFixed(2)};`);
  };
  m.customProgramCacheKey = () => `selfLit${k}`;
  return m;
};
const VIOLET = new THREE.Color('#9a7aa8');

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/** The light: outside at the Grandsire's foot by the hour; inside, the amber through the bark (any hour). */
const OUT: Record<Hour, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string] }> = {
  day: { bg: '#7a8a84', fog: ['#8a968c', 30, 120], hemi: ['#eef2e8', '#2a2a1e', 2.0], key: ['#fff0d8', 2.4], rim: ['#a8c4e8', 1.2], sky: ['#4a6a8a', '#a8b8a8', '#ffe8c0'] },
  dawn: { bg: '#8a8a90', fog: ['#9a96a0', 28, 110], hemi: ['#fff0e0', '#2a2620', 2.1], key: ['#ffd8b0', 2.4], rim: ['#b0c8f0', 1.3], sky: ['#4a5a88', '#c4b0a8', '#ffd8a8'] },
  dusk: { bg: '#4a4050', fog: ['#5e5260', 22, 96], hemi: ['#f0dcd0', '#221a16', 2.0], key: ['#ffbc8c', 2.0], rim: ['#8a98d8', 1.5], sky: ['#202048', '#8a5048', '#ffa060'] },
  night: { bg: '#0e0c12', fog: ['#1a1418', 16, 70], hemi: ['#9290b0', '#100a0a', 1.4], key: ['#b8b0dc', 0.9], rim: ['#d0703a', 1.3], sky: ['#04040a', '#1e1418', '#a8482a'] },
};
const IN = { bg: '#3a2414', fog: ['#4a2e1a', 28, 110] as [string, number, number], hemi: ['#ffd8a8', '#3a2414', 2.3] as [string, string, number], key: ['#ffc888', 2.2] as [string, number], rim: ['#9ad08a', 0.9] as [string, number] };

/** A matrix laying a unit Y-cylinder along a segment (world points), `r` thick. */
const along = (a: THREE.Vector3, b: THREE.Vector3, rx: number, rz = rx): THREE.Matrix4 => {
  const d = new THREE.Vector3().subVectors(b, a);
  const L = Math.max(1e-4, d.length());
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.clone().divideScalar(L));
  return new THREE.Matrix4().compose(a.clone().add(b).multiplyScalar(0.5), q, new THREE.Vector3(rx, L, rz));
};

/** A cheap hash of two numbers in [0, 1). */
const hash = (a: number, b: number): number => {
  const s = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
  return s - Math.floor(s);
};

export class RaicesLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(320);
  private dome: SkyDome | null = null;
  private terrain: TerrainBuild | null = null;
  private now = 0;
  private look = new THREE.Vector3();
  private lastState: BattleState | null = null;
  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private v4 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private c3 = new THREE.Color();
  private e3 = new THREE.Euler();
  private hour: Hour = 'dusk';
  private inside: boolean;
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; flick: number; seed: number }[] = [];
  private tickAt = 0;
  private lastTick = -1;
  // Shared.
  private rings: THREE.InstancedMesh | null = null;
  private veins: THREE.Mesh | null = null;
  // The foot.
  private trunk: THREE.Group | null = null;
  private cleft: THREE.Vector3 | null = null;
  private breath = false;
  private breathT = -1;
  private smokeAt: THREE.Vector3[] = [];
  private shafts: THREE.Mesh | null = null;
  // The gallery.
  private Ml: MouthsLayout | null = null;
  private nodeAt: THREE.Vector3[] = [];
  private knotAt: THREE.Vector3[] = [];
  private mouthAt: THREE.Vector3[] = [];
  private bands: THREE.InstancedMesh | null = null;
  private beads: THREE.InstancedMesh | null = null;
  private beadAt: THREE.Vector3[] = [];
  private beadN = 0;
  private plugs: THREE.InstancedMesh | null = null;
  private opened: THREE.InstancedMesh | null = null;
  private pulseAt = -1;
  // Root nets (a mouth's, the mud root's): each node's world point.
  private netAt: THREE.Vector3[][] = [];
  private netRoots: THREE.InstancedMesh | null = null;
  private sprouts: THREE.InstancedMesh | null = null;
  // The chamber.
  private Cl: ChamberLayout | null = null;
  private flesh: THREE.Mesh | null = null;
  private fleshMat: THREE.MeshStandardMaterial | null = null;
  private croots: THREE.InstancedMesh | null = null;
  /** The four roots' look by kind (wet lumps of mud, amber beads of resin, shards of ice), gone when the root is cut. */
  private cdeco: THREE.InstancedMesh | null = null;
  private cdecoOf: { k: number; m: THREE.Matrix4; c: THREE.Color }[] = [];
  private crootOf: { k: number; vis: boolean }[] = [];
  private crootKey = '';
  private patches: THREE.InstancedMesh | null = null;
  private patchAt: THREE.Vector3[] = [];
  private stems: THREE.InstancedMesh | null = null;
  private caps: THREE.InstancedMesh | null = null;
  private capOf: number[] = [];
  private cellAt: THREE.Vector3[] = [];
  private fnodeAt: THREE.Vector3[] = [];
  private fknotAt: THREE.Vector3[] = [];
  private buds: THREE.InstancedMesh | null = null;
  private bars: THREE.InstancedMesh | null = null;
  private trail: THREE.InstancedMesh | null = null;
  private cone: THREE.Mesh | null = null;
  private rotRing: THREE.Mesh | null = null;
  private gateAt: THREE.Vector3[] = [];
  private wallAt: THREE.Vector3[] = [];
  private cutFlash = [0, 0, 0, 0];
  private gateT: number[] = [0, 0, 0];
  private face: THREE.Group | null = null;
  private mouthCut: THREE.Group | null = null;
  private carveT = -1;
  private handsT = -1;
  private colliersT = -1;
  private lanterns: THREE.Vector3[] = [];

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
    o: { face?: boolean; breath?: boolean } = {},
  ) {
    this.group.name = `raices-${place}`;
    this.inside = place !== 'grandsireFoot' && place !== 'grandsireFallen';
    this.breath = !!o.breath;
    const kit = new Kit();
    const veins = new Kit();
    this.shared();
    if (place === 'grandsireFoot') this.buildFoot(kit, veins, false);
    else if (place === 'grandsireFallen') this.buildFoot(kit, veins, true);
    else if (place === 'sapGallery') this.buildGallery(kit, veins, mouthsLayout(s.seed));
    else if (place === 'sapHollow') this.buildHollow(kit, veins);
    else this.buildChamber(kit, veins, chamberLayout(s.seed), place === 'carcomaMouth', !!o.face);
    const mat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.88, metalness: 0.02, flatShading: true }));
    const dress = kit.build(mat, 'raices-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    // The sap in the wood and the amber through the bark: lit from within (no light of its own).
    const vm = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.5, metalness: 0, emissive: '#ff9a30', emissiveIntensity: 0.6 }));
    this.veins = veins.build(vm, 'raices-veins');
    if (this.veins) {
      this.geos.push(this.veins.geometry);
      this.group.add(this.veins);
    }
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private pt(x: number, y: number, h = 0): THREE.Vector3 {
    return new THREE.Vector3(x * K, h, y * K);
  }

  private hexPt(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.pt(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: new THREE.Color(color), size, a, flick, seed: this.lights.length * 1.37 });
  }

  private inst(geo: THREE.BufferGeometry, mat: THREE.Material, n: number, name: string, order = 0): THREE.InstancedMesh {
    const m = new THREE.InstancedMesh(geo, mat, n);
    m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);
    m.count = 0;
    m.frustumCulled = false;
    m.name = name;
    if (order) m.renderOrder = order;
    this.group.add(m);
    return m;
  }

  private shared(): void {
    const rg = this.track(new THREE.RingGeometry(0.86, 1, 40).rotateX(-Math.PI / 2));
    const rm = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.92, depthWrite: false, side: THREE.DoubleSide }));
    this.rings = this.inst(rg, rm, 32, 'raices-rings', 5);
  }

  /** The floor's tiles over the cells given: a hex slab each, its colour by `paint` (the log's rings, the black earth). */
  private floor(kit: Kit, cells: { q: number; r: number }[], paint: (p: THREE.Vector3, q: number, r: number) => string, h = 0.12): void {
    const hex = new THREE.CylinderGeometry(HEXR * 1.01, HEXR * 1.01, 1, 6);
    for (const c of cells) {
      const p = this.hexPt(c.q, c.r);
      kit.add(hex, paint(p, c.q, c.r), place(p.x, -h / 2, p.z, 0, new THREE.Vector3(1, h, 1)));
    }
    hex.dispose();
  }

  /**
   * A wall of living wood over a rock cell. Deep in the mass (no floor beside it): only its top, a dark slab overlapping
   * its neighbours so the mass reads as one (never a field of hex columns), a crack of amber in it now and then. By the
   * floor (`face`: toward it): a bulge of wood leaning out over the gallery, rounded, its grain down it in dark ridges,
   * a vein of sap up it here and there (lit), a knot with its rings.
   */
  private woodWall(kit: Kit, veins: Kit, q: number, r: number, tall: number, face: THREE.Vector3 | null): void {
    const p = this.hexPt(q, r);
    const k = hash(q, r);
    const box = new THREE.BoxGeometry(1, 1, 1);
    // The mass's top: every cell at the same height, each slab wide enough to overlap its neighbours, so from the
    // game's camera it reads as one plateau of dark wood with the gallery cut into it.
    const top = new THREE.CylinderGeometry(HEXR * 1.25, HEXR * 1.25, 1, 6);
    // (A low step's lid a warmer brown: dark, it outweighed the amber floor beside it.)
    const lid = tall < 1 ? ['#5a3820', '#62402a', '#52341e'] : ['#2a1a0e', '#2e1c10', '#26170c'];
    kit.add(top, lid[Math.floor(k * 2.99)] as string, place(p.x, tall / 2, p.z, 0, new THREE.Vector3(1, tall, 1)));
    // Its grain over the top: long faint ridges, the same way across the whole mass; an amber crack now and then.
    kit.add(box, '#321e10', place(p.x, tall + 0.005, p.z, 0.35 + (k - 0.5) * 0.3, new THREE.Vector3(HEXR * 1.4, 0.01, 0.05)));
    kit.add(box, '#4a2e18', place(p.x + HEXR * 0.3, tall + 0.006, p.z - HEXR * 0.2, 0.3 + (k - 0.5) * 0.4, new THREE.Vector3(HEXR * 0.9, 0.01, 0.04)));
    // A burl now and then: a low knob of wood with its rings, breaking the plateau.
    if (k > 0.3 && k < 0.45) {
      kit.add(new THREE.SphereGeometry(HEXR * 0.55, 9, 5), '#3e2614', place(p.x, tall, p.z, k * 7, new THREE.Vector3(1, 0.32, 0.85)));
      kit.add(new THREE.TorusGeometry(HEXR * 0.32, 0.03, 4, 14).rotateX(Math.PI / 2), '#24160a', place(p.x, tall + 0.14, p.z, k * 7, new THREE.Vector3(1, 1, 0.85)));
    }
    if (k > 0.9) {
      // A short crooked crack, two strokes at an angle (never all one way: a field of them read as rain).
      const a = hash(r, q) * Math.PI;
      veins.add(box, '#ff9a30', place(p.x, tall + 0.012, p.z, a, new THREE.Vector3(HEXR * 0.55, 0.012, 0.045)));
      veins.add(box, '#ff9a30', place(p.x + Math.cos(a) * HEXR * 0.3, tall + 0.012, p.z - Math.sin(a) * HEXR * 0.3, a + 0.7, new THREE.Vector3(HEXR * 0.35, 0.012, 0.04)));
    }
    if (!face) {
      top.dispose();
      box.dispose();
      return;
    }
    const f = face.clone().normalize();
    const yaw = Math.atan2(f.x, f.z);
    const side = new THREE.Vector3(f.z, 0, -f.x);
    // By the floor: the wall's face bulging out under the top's lip, rounded, lighter where the amber falls on it.
    const c = p.clone().addScaledVector(f, HEXR * 0.32);
    kit.add(new THREE.SphereGeometry(HEXR * 0.95, 10, 6), WOOD[Math.floor(k * 4.99)] as string, place(c.x, tall * 0.48, c.z, k * 6, new THREE.Vector3(1.05, (tall * 0.55) / HEXR, 0.8)));
    kit.add(box, '#4e3020', place(p.x + f.x * HEXR * 0.9, tall - 0.04, p.z + f.z * HEXR * 0.9, yaw, new THREE.Vector3(HEXR * 1.7, 0.08, 0.16)));
    // Grain: dark ridges down the face, a little crooked.
    for (let i = -2; i <= 2; i++) {
      const g = c.clone().addScaledVector(f, HEXR * (0.78 - Math.abs(i) * 0.12)).addScaledVector(side, i * HEXR * 0.3);
      kit.add(box, '#1e1208', place(g.x, tall * 0.48, g.z, yaw, new THREE.Vector3(0.05, tall * 0.8, 0.06), 0, (hash(q + i, r) - 0.5) * 0.2));
    }
    if (k > 0.45) {
      const v = c.clone().addScaledVector(f, HEXR * 0.86).addScaledVector(side, (k - 0.7) * HEXR);
      veins.add(box, '#ffb050', place(v.x, tall * 0.45, v.z, yaw, new THREE.Vector3(0.06, tall * 0.7, 0.04), 0, (k - 0.7) * 0.4));
      if (k > 0.8) this.light(v.clone().setY(tall * 0.5), '#ffa040', 1.4, 0.32, 0.5);
    }
    if (k < 0.2) {
      const kn = c.clone().addScaledVector(f, HEXR * 0.86).setY(tall * 0.5);
      kit.add(new THREE.TorusGeometry(0.15, 0.05, 5, 12), '#1a0e06', place(kn.x, kn.y, kn.z, yaw, new THREE.Vector3(1, 1.3, 1)));
      kit.add(new THREE.SphereGeometry(0.1, 7, 5), '#5a3a22', place(kn.x, kn.y, kn.z, yaw, new THREE.Vector3(1, 1.3, 0.5)));
    }
    top.dispose();
    box.dispose();
  }

  /** Every rock cell of the board as wood (the ones by the floor bulging out, with their face to it). */
  private walls(kit: Kit, veins: Kit, rocks: { q: number; r: number }[], floor: Set<string>, tall: number, skip?: (p: THREE.Vector3) => boolean): void {
    const D = [
      [1, 0],
      [1, -1],
      [0, -1],
      [-1, 0],
      [-1, 1],
      [0, 1],
    ];
    for (const c of rocks) {
      const p = this.hexPt(c.q, c.r);
      if (skip?.(p)) continue;
      let face: THREE.Vector3 | null = null;
      for (const [dq, dr] of D) {
        if (!floor.has(`${c.q + (dq as number)},${c.r + (dr as number)}`)) continue;
        const n = this.hexPt(c.q + (dq as number), c.r + (dr as number));
        face = (face ?? new THREE.Vector3()).add(n.sub(p));
      }
      this.woodWall(kit, veins, c.q, c.r, tall, face && face.lengthSq() > 1e-6 ? face : null);
    }
  }

  /** A great root humped over the ground from a to b (half buried), its bark, moss on its back. */
  private bigRoot(kit: Kit, a: THREE.Vector3, b: THREE.Vector3, r: number, seed: number): void {
    const cyl = new THREE.CylinderGeometry(1, 1.15, 1, 9);
    const n = 4;
    for (let i = 0; i < n; i++) {
      const t0 = i / n;
      const t1 = (i + 1) / n;
      const pa = a.clone().lerp(b, t0);
      const pb = a.clone().lerp(b, t1);
      const rr = r * (1 - t0 * 0.6);
      pa.y = rr * 0.35 * Math.sin(Math.PI * (0.2 + t0 * 0.8));
      pb.y = rr * 0.6 * 0.35 * Math.sin(Math.PI * (0.2 + t1 * 0.8));
      kit.add(cyl, BARK[(seed + i) % BARK.length] as string, along(pa, pb, rr, rr * 0.85));
      if ((seed + i) % 2 === 0) kit.add(new THREE.SphereGeometry(1, 7, 4), MOSS[(seed + i) % MOSS.length] as string, place((pa.x + pb.x) / 2, pa.y + rr * 0.8, (pa.z + pb.z) / 2, seed, new THREE.Vector3(rr * 0.9, rr * 0.2, rr * 1.2)));
    }
    cyl.dispose();
  }

  /** A tree of the wood round the Grandsire: a dark trunk, a crown of deep greens. */
  private tree(kit: Kit, p: THREE.Vector3, k: number): void {
    const cyl = new THREE.CylinderGeometry(0.12, 0.2, 1, 7);
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    const h = 2.6 + k * 2;
    kit.add(cyl, BARK[Math.floor(k * 3.99)] as string, place(p.x, h / 2, p.z, k * 5, new THREE.Vector3(1.1, h, 1.1)));
    for (let i = 0; i < 6; i++) {
      const a = i * 1.7 + k * 3;
      const rr = 0.45 + (i % 3) * 0.25;
      kit.add(ico, MOSS[(i + Math.floor(k * 7)) % MOSS.length] as string, place(p.x + Math.cos(a) * rr * 0.6, h - 0.3 + (i % 3) * 0.35, p.z + Math.sin(a) * rr * 0.6, a, 1.0 + (i % 2) * 0.45));
    }
    cyl.dispose();
    ico.dispose();
  }

  /** Olmo's face carved in wood: the oval smoothed, the eyes cut deep; the mouth and the line under it in their own group (the close carves them). */
  private carvedFace(kit: Kit, p: THREE.Vector3, yaw: number, size: number, mouth: boolean): THREE.Group {
    const sph = new THREE.SphereGeometry(0.42, 14, 9);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const fx = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw).add(p);
    const cut = '#1a0e08';
    kit.add(sph, '#a07a56', place(p.x, p.y, p.z, yaw, new THREE.Vector3(1.4 * size, 1.9 * size, 0.3 * size)));
    kit.add(sph, '#7a5a3e', place(fx(0, 0, -0.03 * size).x, p.y, fx(0, 0, -0.03 * size).z, yaw, new THREE.Vector3(1.5 * size, 2.0 * size, 0.28 * size)));
    for (const k of [-1, 1]) {
      const e = fx(k * 0.18 * size, 0.16 * size, 0.11 * size);
      kit.add(sph, cut, place(e.x, e.y, e.z, yaw, new THREE.Vector3(0.24 * size, 0.1 * size, 0.08 * size)));
      const b = fx(k * 0.18 * size, 0.27 * size, 0.11 * size);
      kit.add(box, '#5a3e28', place(b.x, b.y, b.z, yaw, new THREE.Vector3(0.24 * size, 0.035 * size, 0.05 * size), 0, -k * 0.2));
    }
    const nose = fx(0, -0.02 * size, 0.12 * size);
    kit.add(box, '#8a6644', place(nose.x, nose.y, nose.z, yaw, new THREE.Vector3(0.07 * size, 0.24 * size, 0.07 * size)));
    // The mouth and the line under it: their own pieces (lit a little), shown when the hero carves them.
    const g = new THREE.Group();
    g.name = 'raices-face-mouth';
    const mm = this.track(new THREE.MeshStandardMaterial({ color: '#1a0e08', roughness: 0.9 }));
    const lm = this.track(new THREE.MeshStandardMaterial({ color: '#ffd8a0', roughness: 0.6, emissive: '#ff9a40', emissiveIntensity: 0.6 }));
    const mg = this.track(new THREE.BoxGeometry(1, 1, 1));
    const mo = new THREE.Mesh(mg, mm);
    mo.position.copy(fx(0, -0.3 * size, 0.12 * size));
    mo.rotation.y = yaw;
    mo.scale.set(0.34 * size, 0.05 * size, 0.06 * size);
    g.add(mo);
    // The line under it: two short strokes of fresh wood, cut and glowing.
    for (let i = 0; i < 2; i++) {
      const l = new THREE.Mesh(mg, lm);
      l.position.copy(fx(0, -(0.68 + i * 0.14) * size, 0.07 * size));
      l.rotation.y = yaw;
      l.scale.set((0.56 - i * 0.18) * size, 0.04 * size, 0.04 * size);
      g.add(l);
    }
    g.visible = mouth;
    this.group.add(g);
    sph.dispose();
    box.dispose();
    return g;
  }

  /* ---------- outside: the Grandsire's foot ---------- */

  private buildFoot(kit: Kit, veins: Kit, fallen: boolean): void {
    const R = this.map.radius;
    const cells: { q: number; r: number }[] = [];
    for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) <= R) cells.push({ q, r });
    // The wood's floor: moss and leaf mould, the path down to the south trodden dark.
    // (Close shades: a mosaic of light and dark tiles read as a board, not a wood floor.)
    const FLOOR = ['#3a5228', '#3e5629', '#37502a', '#41592c'];
    const PATH = ['#3a2e22', '#3e3124', '#362a20', '#40332a'];
    this.floor(kit, cells, (p, q, r) => {
      const onPath = p.z > 0 && Math.abs(p.x - Math.sin(p.z * 0.35) * 0.8) < 0.9;
      return onPath ? (PATH[(q * 3 + r) & 3] as string) : (FLOOR[Math.floor(hash(q, r) * 3.99)] as string);
    });
    const C = this.pt(0, fallen ? -6500 : -4800);
    const TR = 4.1 * S;
    if (!fallen) {
      // The trunk: a great column of furrowed bark, a cleft at its front that breathes (amber inside, its own group).
      const g = new THREE.Group();
      g.name = 'raices-trunk';
      const tk = new Kit();
      const cyl = new THREE.CylinderGeometry(1, 1.12, 1, 22);
      tk.add(cyl, BARK[1] as string, place(C.x, 6, C.z, 0, new THREE.Vector3(TR, 14, TR)));
      for (let i = 0; i < 22; i++) {
        const a = (i / 22) * Math.PI * 2;
        tk.add(cyl, BARK[i % BARK.length] as string, place(C.x + Math.cos(a) * TR * 1.0, 6, C.z + Math.sin(a) * TR * 1.0, 0, new THREE.Vector3(TR * 0.09, 14, TR * 0.09)));
      }
      const tm = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.92, flatShading: true }));
      const tmesh = tk.build(tm, 'raices-trunk-bark');
      if (tmesh) {
        tmesh.castShadow = true;
        this.geos.push(tmesh.geometry);
        g.add(tmesh);
      }
      this.group.add(g);
      this.trunk = g;
      cyl.dispose();
      // The cleft at its front (south): two lips of bark, amber deep inside.
      const front = C.clone().add(new THREE.Vector3(0, 0, TR * 0.98));
      this.cleft = front.clone().setY(2.2);
      const box = new THREE.BoxGeometry(1, 1, 1);
      veins.add(box, '#ffb050', place(front.x, 2.2, front.z - 0.05, 0, new THREE.Vector3(0.5, 4.2, 0.1)));
      veins.add(box, '#ffc878', place(front.x, 1.6, front.z, 0, new THREE.Vector3(0.22, 2.4, 0.08)));
      // Amber through the bark's cracks, all round its foot.
      for (let i = 0; i < 9; i++) {
        const a = Math.PI / 2 + (i - 4) * 0.22;
        const v = C.clone().add(new THREE.Vector3(Math.cos(a) * TR * 1.01, 0, Math.sin(a) * TR * 1.01));
        veins.add(box, '#ff9a30', place(v.x, 1.3 + hash(i, 3) * 2.2, v.z, -a + Math.PI / 2, new THREE.Vector3(0.05, 0.9 + hash(i, 5) * 1.6, 0.05), 0, (hash(i, 7) - 0.5) * 0.5));
      }
      box.dispose();
      // Its roots, humped over the ground and away into the wood.
      // (Off to the sides: in front, where the people stand, the ground stays clear.)
      for (let i = 0; i < 10; i++) {
        const a = Math.PI * -0.15 + (i / 9) * Math.PI * 1.3;
        if (Math.abs(a - Math.PI / 2) < 0.38) continue;
        const a0 = C.clone().add(new THREE.Vector3(Math.cos(a) * TR * 0.95, 0, Math.sin(a) * TR * 0.95));
        const b0 = C.clone().add(new THREE.Vector3(Math.cos(a) * TR * (1.35 + hash(i, 1) * 0.35), 0, Math.sin(a) * TR * (1.35 + hash(i, 1) * 0.35)));
        this.bigRoot(kit, a0, b0, 0.32 + hash(i, 2) * 0.14, i);
      }
      this.light(front.clone().setY(1.8), '#ffa040', 3.2, 0.5, 0.6);
    } else {
      // Lying over the wood: the trunk on its side across the north, its stump torn, the light coming in.
      const cyl = new THREE.CylinderGeometry(1, 1.05, 1, 18);
      const a = this.pt(-11000, -5200, TR * 0.7);
      const b = this.pt(11000, -7600, TR * 0.6);
      kit.add(cyl, BARK[1] as string, along(a, b, TR * 0.75));
      for (let i = 0; i < 12; i++) {
        const t = i / 11;
        const p = a.clone().lerp(b, t);
        kit.add(new THREE.SphereGeometry(1, 8, 5), MOSS[i % MOSS.length] as string, place(p.x, p.y + TR * 0.65, p.z, i, new THREE.Vector3(1.2, 0.35, 1.0)));
      }
      // The stump, torn: jagged splinters of pale wood.
      const st = this.pt(-3200, -3600);
      kit.add(cyl, BARK[2] as string, place(st.x, 1.0, st.z, 0, new THREE.Vector3(2.2, 2.0, 2.2)));
      const cone = new THREE.ConeGeometry(0.4, 1, 5);
      for (let i = 0; i < 9; i++) {
        const an = (i / 9) * Math.PI * 2;
        kit.add(cone, i % 2 ? '#a07a50' : '#7a5636', place(st.x + Math.cos(an) * 1.5, 2.2 + hash(i, 9) * 0.6, st.z + Math.sin(an) * 1.5, an, new THREE.Vector3(1, 1.2 + hash(i, 4), 1)));
      }
      cone.dispose();
      cyl.dispose();
      // The shoot, a hand high, in its ring of stones, right by where the people stand.
      const sh = this.pt(-1300, 1300);
      const stone = new THREE.DodecahedronGeometry(0.16, 0);
      for (let i = 0; i < 9; i++) {
        const an = (i / 9) * Math.PI * 2;
        kit.add(stone, ['#5e6066', '#6a6c72', '#54565c'][i % 3] as string, place(sh.x + Math.cos(an) * 0.42, 0.06, sh.z + Math.sin(an) * 0.42, an, 1));
      }
      stone.dispose();
      kit.add(new THREE.CylinderGeometry(0.02, 0.03, 0.35, 5), '#4a6a2a', place(sh.x, 0.17, sh.z));
      for (const k of [-1, 1]) kit.add(new THREE.SphereGeometry(0.08, 8, 5), '#8ee060', place(sh.x + k * 0.07, 0.34, sh.z, k, new THREE.Vector3(1.1, 0.3, 0.6), 0, k * 0.5));
      this.light(sh.clone().setY(0.4), '#b8f090', 1.2, 0.45);
      // The light in: tall pale shafts down through the hole in the canopy (one additive mesh).
      const sg = this.track(new THREE.PlaneGeometry(1, 1));
      const sm = this.track(new THREE.MeshBasicMaterial({ color: '#fff2d8', transparent: true, opacity: 0.12, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }));
      const sk = new Kit();
      for (let i = 0; i < 7; i++) {
        const x = -6 + i * 2 + hash(i, 1);
        sk.add(sg, '#ffffff', place(x, 5, -1 + hash(i, 2) * 3, 0.6, new THREE.Vector3(1.2 + hash(i, 3), 12, 1), 0, -0.35));
      }
      this.shafts = sk.build(sm, 'raices-shafts');
      if (this.shafts) {
        this.geos.push(this.shafts.geometry);
        this.group.add(this.shafts);
      }
    }
    // The wood round it: trees at the rim, more to the sides.
    for (const c of cells) {
      const d = Math.max(Math.abs(c.q), Math.abs(c.r), Math.abs(c.q + c.r));
      const p = this.hexPt(c.q, c.r);
      if (d < R - 1 || p.distanceTo(C) < TR * 1.25) continue;
      if (Math.abs(p.x - Math.sin(p.z * 0.35) * 0.8) < 1.4 && p.z > 0) continue;
      if (hash(c.q, c.r) < 0.55) this.tree(kit, p, hash(c.r, c.q));
    }
    // Smoke up the path from the colliers' lanterns, and the lanterns' embers.
    for (let i = 0; i < 3; i++) {
      const z = (R - 1.2 - i * 0.9) * 0.866 * S;
      const p = new THREE.Vector3(Math.sin(z * 0.35) * 0.8 + (i - 1) * 0.6, 0, z);
      this.smokeAt.push(p);
    }
  }

  /* ---------- act 2: the gallery of the buds below ---------- */

  private buildGallery(kit: Kit, veins: Kit, L: MouthsLayout): void {
    this.Ml = L;
    const floorSet = new Set(L.floor.map((h) => `${h.q},${h.r}`));
    const ctr = this.pt(L.foot.x, L.foot.y);
    // The heartwood cut in rings, like a log, from the gallery's top.
    this.floor(kit, L.floor, (p) => {
      const d = Math.hypot(p.x - ctr.x, p.z - ctr.z);
      const band = Math.floor(d / (0.55 * S)) % 2;
      return HEART[band * 2 + (Math.floor(d * 3.1) % 2)] as string;
    });
    // (A thick step of wood, low: a taller mass hid the grooves and the nets from the game's camera on a phone.)
    this.walls(kit, veins, L.rocks, floorSet, 0.55);
    this.nodeAt = L.nodes.map((n) => this.pt(n.x, n.y));
    this.knotAt = L.knots.map((k) => this.pt(k.x, k.y));
    this.mouthAt = L.mouths.map((m) => this.nodeAt[m] as THREE.Vector3);
    const foot = this.pt(L.foot.x, L.foot.y);
    // The sap's grooves: a dark channel cut in the floor along every branch, amber at its bottom (lit).
    const box = new THREE.BoxGeometry(1, 1, 1);
    const groove = (a: THREE.Vector3, b: THREE.Vector3, wide: number): void => {
      const d = new THREE.Vector3().subVectors(b, a);
      const len = d.length();
      const yaw = Math.atan2(d.x, d.z);
      const m = a.clone().add(b).multiplyScalar(0.5);
      kit.add(box, '#160c06', place(m.x, 0.005, m.z, yaw, new THREE.Vector3(wide, 0.03, len)));
      kit.add(box, '#3a2212', place(m.x, 0.012, m.z, yaw, new THREE.Vector3(wide * 0.7, 0.03, len * 0.98)));
      veins.add(box, '#ff9a30', place(m.x, 0.02, m.z, yaw, new THREE.Vector3(wide * 0.18, 0.02, len * 0.96)));
    };
    groove(foot, this.nodeAt[0] as THREE.Vector3, 0.62);
    L.nodes.forEach((n, i) => {
      if (n.parent >= 0) groove(this.nodeAt[n.parent] as THREE.Vector3, this.nodeAt[i] as THREE.Vector3, 0.5);
    });
    // The foot: where the sap comes down out of the wood, a spring of amber in a knot of the wall.
    kit.add(new THREE.CylinderGeometry(0.9, 1.1, 0.3, 10), '#2a1a0e', place(foot.x, 0.1, foot.z));
    veins.add(new THREE.SphereGeometry(0.5, 10, 6), '#ffb848', place(foot.x, 0.18, foot.z, 0, new THREE.Vector3(1.2, 0.3, 1.2)));
    this.light(foot.clone().setY(0.6), '#ffae3c', 3.0, 0.6, 0.4);
    // The side galleries that drink: dark holes in a lump of wood, a stain of sap.
    L.nodes.forEach((n, i) => {
      if (n.need || n.kids.length) return;
      const p = this.nodeAt[i] as THREE.Vector3;
      kit.add(new THREE.SphereGeometry(0.7, 10, 6), '#2e1c10', place(p.x, 0.15, p.z, 0, new THREE.Vector3(1.1, 0.5, 1.1)));
      kit.add(new THREE.CircleGeometry(0.42, 12).rotateX(-Math.PI / 2), '#080404', place(p.x, 0.42, p.z));
      veins.add(new THREE.CircleGeometry(0.55, 12).rotateX(-Math.PI / 2), '#a8601c', place(p.x, 0.02, p.z));
    });
    // The forks: a knob of wood where the groove splits.
    L.nodes.forEach((n, i) => {
      if (n.need || !n.kids.length) return;
      const p = this.nodeAt[i] as THREE.Vector3;
      kit.add(new THREE.CylinderGeometry(0.55, 0.65, 0.16, 10), '#3a2414', place(p.x, 0.06, p.z));
      veins.add(new THREE.CylinderGeometry(0.22, 0.22, 0.04, 10), '#ffa040', place(p.x, 0.15, p.z));
    });
    // Each mouth's notches: a dark slab standing beside it, its bars pale and raised (fours grouped, so twelve reads).
    L.mouths.forEach((m) => {
      const n = L.nodes[m] as DeepSapNode;
      const p = this.nodeAt[m] as THREE.Vector3;
      const sx = p.x + (p.x >= 0 ? 1.25 : -1.25);
      const sz = p.z - 0.45;
      const groups = Math.ceil(n.need / 4);
      const wd = 0.3 + n.need * 0.13 + (groups - 1) * 0.1;
      const tilt = -0.32;
      kit.add(box, '#0a0605', place(sx, 0.5, sz - 0.03, 0, new THREE.Vector3(wd + 0.16, 0.9, 0.08), tilt));
      kit.add(box, '#2a1a12', place(sx, 0.5, sz, 0, new THREE.Vector3(wd, 0.76, 0.1), tilt));
      for (let k = 0; k < n.need; k++) {
        const bx = sx - (wd - 0.3) / 2 + k * 0.13 + Math.floor(k / 4) * 0.1;
        kit.add(box, '#f0e2c8', place(bx, 0.52, sz + 0.06, 0, new THREE.Vector3(0.07, 0.5, 0.06), tilt));
      }
      // The mouth's lip: a ring of bark round the hole in the floor.
      kit.add(new THREE.TorusGeometry(0.72, 0.18, 6, 16).rotateX(Math.PI / 2), '#3a2414', place(p.x, 0.08, p.z));
    });
    // The root nets under the mouths.
    this.buildNets(kit, L.nets.map((m) => m.net.nodes.map((h) => this.hexPt(h.q, h.r))));
    box.dispose();
    // The knots' bands, the beads, the plugs, the open mouths.
    const tg = this.track(new THREE.TorusGeometry(0.4, 0.13, 6, 14));
    const tm = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.6, flatShading: true }));
    this.bands = this.inst(tg, tm, Math.max(1, L.knots.length + 8), 'raices-bands');
    const bg = this.track(new THREE.SphereGeometry(0.17, 8, 6));
    const bm = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff' }));
    this.beads = this.inst(bg, bm, 90, 'raices-beads', 3);
    const pg = this.track(new THREE.SphereGeometry(0.62, 12, 8));
    const pm = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.4, metalness: 0.1, flatShading: true, emissive: '#2a1430', emissiveIntensity: 0.5 }));
    this.plugs = this.inst(pg, pm, 8, 'raices-plugs');
    const og = this.track(new THREE.TorusGeometry(0.6, 0.12, 6, 18).rotateX(Math.PI / 2));
    const om = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.5, emissive: '#3a7a20', emissiveIntensity: 0.6 }));
    this.opened = this.inst(og, om, 8, 'raices-open');
  }

  /** A root net's stones (with volume), the two sound roots' stumps where they come up, the mouth; its roots and moss (instanced). */
  private buildNets(kit: Kit, nets: THREE.Vector3[][]): void {
    this.netAt = nets;
    const stone = new THREE.DodecahedronGeometry(0.42, 0);
    for (const N of nets)
      N.forEach((p, i) => {
        if (i === 2) return;
        if (i < 2) {
          // A sound root's stump where it comes up out of the floor: pale, thick, Olmo's notch on it.
          kit.add(new THREE.CylinderGeometry(0.26, 0.38, 0.6, 8), '#6e5a34', place(p.x, 0.25, p.z, i, 1, 0.2));
          kit.add(new THREE.BoxGeometry(0.2, 0.06, 0.1), '#e6d8b8', place(p.x, 0.56, p.z + 0.1, 0.4));
          return;
        }
        kit.add(stone, ['#7a7670', '#86827a', '#6e6a64'][i % 3] as string, place(p.x, 0.18, p.z, i * 1.3, new THREE.Vector3(1.15, 0.6, 1.0)));
        kit.add(stone, '#9a958c', place(p.x + 0.08, 0.36, p.z - 0.05, i * 0.7, new THREE.Vector3(0.6, 0.28, 0.55)));
      });
    stone.dispose();
    const cg = this.track(new THREE.CylinderGeometry(1, 1, 1, 7).translate(0, 0.5, 0));
    const cm = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.6, flatShading: true }));
    this.netRoots = this.inst(cg, cm, 48, 'raices-net-roots');
    this.netRoots.castShadow = true;
    const sg = this.track(new THREE.ConeGeometry(0.16, 0.5, 5).translate(0, 0.25, 0));
    const sm = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.6, emissive: '#2a5a14', emissiveIntensity: 0.6, flatShading: true }));
    this.sprouts = this.inst(sg, sm, 24, 'raices-sprouts');
  }

  /** The roots of each net as they grow: every piece of each tip's trail, and the one it is growing now (a share of it). */
  private drawNets(s: BattleState, live: (i: number) => boolean): void {
    const roots = this.netRoots;
    const sp = this.sprouts;
    const w = s.senda?.deep;
    if (!roots || !sp || !w) return;
    let n = 0;
    let m = 0;
    const f = this.frac();
    const piece = (a: THREE.Vector3, b: THREE.Vector3, r: number, c: THREE.Color): void => {
      if (n >= roots.instanceMatrix.count) return;
      const d = this.v4.subVectors(b, a);
      const len = Math.max(1e-4, d.length());
      this.q4.setFromUnitVectors(this.s3.set(0, 1, 0), d.divideScalar(len));
      this.m4.compose(this.v3.copy(a).setY(0.16), this.q4, this.s3.set(r, len, r));
      roots.setMatrixAt(n, this.m4);
      roots.setColorAt(n++, c);
    };
    this.netAt.forEach((N, i) => {
      const st = w.roots[i] as RootsState | undefined;
      if (!st || !live(i)) return;
      for (let k = 3; k < N.length; k++) {
        if (!(st.sprout & (1 << k)) || m >= sp.instanceMatrix.count) continue;
        const p = N[k] as THREE.Vector3;
        this.m4.compose(this.v3.set(p.x, 0.4, p.z), this.q4.identity(), this.s3.set(1, 1, 1));
        sp.setMatrixAt(m, this.m4);
        sp.setColorAt(m++, GREEN);
      }
      for (const tip of st.tips) {
        const col = tip.mode === 2 ? TANGLE : tip.mode === 3 ? this.c3.set('#9ab84a') : SOUND;
        for (let j = 1; j < tip.trail.length; j++) piece(N[tip.trail[j - 1] as number] as THREE.Vector3, N[tip.trail[j] as number] as THREE.Vector3, 0.15, col);
        if (tip.mode === 1 && tip.to >= 0) {
          const k = Math.max(0, Math.min(1, (s.tick + f - tip.t0) / Math.max(1, tip.arr - tip.t0)));
          const a = N[tip.at] as THREE.Vector3;
          const b = this.v3.lerpVectors(a, N[tip.to] as THREE.Vector3, k).clone();
          piece(a, b, 0.15, col);
        }
      }
    });
    roots.count = n;
    roots.instanceMatrix.needsUpdate = true;
    if (roots.instanceColor) roots.instanceColor.needsUpdate = true;
    sp.count = m;
    sp.instanceMatrix.needsUpdate = true;
    if (sp.instanceColor) sp.instanceColor.needsUpdate = true;
  }

  /** The way a bead of sap goes from the foot down to a node (world points). */
  private pathTo(nodes: DeepSapNode[], at: THREE.Vector3[], foot: THREE.Vector3, i: number): THREE.Vector3[] {
    const out: THREE.Vector3[] = [];
    for (let c = i; c >= 0; c = (nodes[c] as DeepSapNode).parent) out.unshift(at[c] as THREE.Vector3);
    out.unshift(foot);
    return out;
  }

  /**
   * The beads of a flow (the gallery's, or the frost root's): each pulse from the foot along every open branch to the
   * leaf it feeds, as big as its share, stopping at a shut knot. And the knots' bands. `skip`: leaves that drink no more.
   */
  private drawFlow(s: BattleState, nodes: DeepSapNode[], knots: { node: number }[], at: THREE.Vector3[], kat: THREE.Vector3[], foot: THREE.Vector3, closed: number[], pulseNext: number, pulse: number, start: { b: number; k: number }): { b: number; k: number } {
    const beads = this.beads;
    const bands = this.bands;
    let nb = start.b;
    let nk = start.k;
    if (bands)
      knots.forEach((k, i) => {
        if (nk >= bands.instanceMatrix.count) return;
        const node = nodes[k.node] as DeepSapNode;
        const par = (node.parent >= 0 ? nodes[node.parent] : null) as DeepSapNode | null;
        const p = kat[i] as THREE.Vector3;
        const yaw = par ? Math.atan2(node.x - par.x, node.y - par.y) : 0;
        const shut = closed[i] === 1;
        // Shut: a thick black band clenched upright across the groove. Open: undone, lying flat beside it, pale.
        if (shut) this.m4.compose(this.v3.set(p.x, 0.22, p.z), this.q4.setFromEuler(this.e3.set(0, yaw, 0)), this.s3.set(1.5, 1.5, 2.0));
        else this.m4.compose(this.v3.set(p.x + Math.cos(yaw) * 0.7, 0.05, p.z - Math.sin(yaw) * 0.7), this.q4.setFromEuler(this.e3.set(Math.PI / 2, 0, 0)), this.s3.set(0.85, 0.85, 0.45));
        bands.setMatrixAt(nk, this.m4);
        bands.setColorAt(nk++, this.c3.set(shut ? '#0a0606' : '#c89a64'));
      });
    if (beads) {
      const flow = sapFlow(nodes, knots, 0, closed.map((x) => x === 1));
      const since = (s.tick - (pulseNext - pulse) + this.frac()) / Math.max(1, pulse);
      const shut = new Set<number>();
      knots.forEach((k, i) => closed[i] && shut.add(k.node));
      nodes.forEach((node, i) => {
        if (node.kids.length || nb >= beads.instanceMatrix.count) return;
        const way = this.pathTo(nodes, at, foot, i);
        let stop = way.length - 1;
        let depth = 0;
        // (A shut knot on the branch into a node stops the bead at the node before it.)
        for (let c = i; c >= 0; c = (nodes[c] as DeepSapNode).parent, depth++) if (shut.has(c)) stop = Math.min(stop, way.length - 2 - depth);
        let share = 12;
        for (let c = node.parent; c >= 0; c = (nodes[c] as DeepSapNode).parent) share /= Math.max(1, (nodes[c] as DeepSapNode).kids.filter((k) => !shut.has(k)).length);
        const legs = Math.max(1, stop);
        const t = Math.min(0.999, since) * legs;
        const leg = Math.floor(t);
        const pa = way[leg];
        const pb = way[Math.min(way.length - 1, leg + 1)];
        if (!pa || !pb) return;
        this.v3.lerpVectors(pa, pb, t - leg).setY(0.2);
        const sc = 0.7 + Math.min(1.8, share / 3);
        this.m4.compose(this.v3, this.q4.identity(), this.s3.set(sc, sc, sc));
        beads.setMatrixAt(nb, this.m4);
        (this.beadAt[nb] ??= new THREE.Vector3()).copy(this.v3);
        beads.setColorAt(nb++, flow[i] === 1 ? WARN2 : AMBER);
      });
    }
    return { b: nb, k: nk };
  }

  private drawGallery(s: BattleState): void {
    const L = this.Ml;
    const r = s.rules?.senda?.deep?.mouths;
    const w = s.senda?.deep;
    if (!L || !r || !w || !w.closed.length) return;
    const foot = this.pt(L.foot.x, L.foot.y);
    const out = this.drawFlow(s, r.nodes, r.knots, this.nodeAt, this.knotAt, foot, w.closed, w.pulseNext, r.pulse, { b: 0, k: 0 });
    this.finish(this.beads, w.done >= 0 ? 0 : out.b);
    this.beadN = w.done >= 0 ? 0 : out.b;
    this.finish(this.bands, out.k);
    // The mouths: the plug of rot while shut (violet-black, swollen; cracked: amber running), the ring once open.
    let a = 0;
    let b = 0;
    L.mouths.forEach((m, k) => {
      const p = this.nodeAt[m] as THREE.Vector3;
      const open = (w.open[k] ?? -1) >= 0;
      if (!open && this.plugs) {
        const cracked = (w.crackUntil[m] ?? 0) > s.tick;
        const breathe = 1 + 0.05 * Math.sin(this.now * 2.2 + k);
        this.m4.compose(this.v3.set(p.x, 0.22, p.z), this.q4.identity(), this.s3.set(breathe, 0.65 * breathe, breathe));
        this.plugs.setMatrixAt(a, this.m4);
        this.plugs.setColorAt(a++, cracked ? this.c3.set('#a8601c') : ROT);
      } else if (this.opened) {
        this.m4.compose(this.v3.set(p.x, 0.14, p.z), this.q4.identity(), this.s3.set(1, 1, 1));
        this.opened.setMatrixAt(b, this.m4);
        this.opened.setColorAt(b++, (w.clean[k] ?? -1) >= 0 ? GREEN : this.c3.set('#d8a050'));
      }
    });
    this.finish(this.plugs, a);
    this.finish(this.opened, b);
    this.drawNets(s, (i) => (w.open[i] ?? -1) >= 0);
  }

  private finish(m: THREE.InstancedMesh | null, n: number): void {
    if (!m) return;
    m.count = n;
    m.instanceMatrix.needsUpdate = true;
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
  }

  /* ---------- the campfire's hollow ---------- */

  private buildHollow(kit: Kit, veins: Kit): void {
    const R = this.map.radius;
    const cells: { q: number; r: number }[] = [];
    const rocks: { q: number; r: number }[] = [];
    for (let r = -R; r <= R; r++)
      for (let q = -R; q <= R; q++) {
        const d = Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
        if (d > R) continue;
        (d <= 3 ? cells : rocks).push({ q, r });
      }
    this.floor(kit, cells, (p) => HEART[Math.floor(Math.hypot(p.x, p.z) / (0.4 * S)) % 4] as string);
    this.walls(kit, veins, rocks, new Set(cells.map((h) => `${h.q},${h.r}`)), 2.2);
    // Sap dripping from the roof's cracks, glowing in the dark round the fire.
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + 0.3;
      const p = new THREE.Vector3(Math.cos(a) * 3.3 * S * 0.86, 1.6 + hash(i, 1) * 1.4, Math.sin(a) * 3.3 * S * 0.86);
      this.light(p, '#ffa040', 1.6, 0.35, 0.6);
    }
  }

  /* ---------- act 4: the Carcoma's chamber ---------- */

  private buildChamber(kit: Kit, veins: Kit, L: ChamberLayout, mouth: boolean, face: boolean): void {
    this.Cl = L;
    const R = L.radius;
    const rockSet = new Set(L.rocks.map((h) => `${h.q},${h.r}`));
    const cells: { q: number; r: number }[] = [];
    for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) <= R && !rockSet.has(`${q},${r}`)) cells.push({ q, r });
    // Black earth between roots; the way down the middle to the door trodden a shade lighter.
    this.floor(kit, cells, (p, q, r) => (Math.abs(p.x) < 0.7 && p.z > 0.4 ? '#3a2e26' : (EARTH[Math.floor(hash(q, r) * 3.99)] as string)));
    // Thin roots lying over the earth, for texture.
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 6);
    for (let i = 0; i < 22; i++) {
      const a = this.pt((hash(i, 1) - 0.5) * 16000, (hash(i, 2) - 0.5) * 15000, 0.05);
      const b = a.clone().add(new THREE.Vector3((hash(i, 3) - 0.5) * 3, 0, (hash(i, 4) - 0.5) * 3));
      if (Math.hypot(a.x, a.z) > (R - 1.5) * S * 0.86) continue;
      kit.add(cyl, '#1e1612', along(a, b, 0.04 + hash(i, 5) * 0.03));
    }
    // The wall: living wood round, and at its back (the north) the titan's flesh, smooth, dark red, beating (its own mesh).
    const fk = new Kit();
    const sph = new THREE.SphereGeometry(1, 12, 8);
    const flesh: { q: number; r: number }[] = [];
    for (const h of L.rocks) if (this.hexPt(h.q, h.r).z < -5.0 * S * 0.866) flesh.push(h);
    for (const h of flesh) {
      const p = this.hexPt(h.q, h.r);
      const k = hash(h.q, h.r);
      fk.add(sph, FLESH[Math.floor(k * 3.99)] as string, place(p.x, 1.2 + k * 0.6, p.z, k * 4, new THREE.Vector3(HEXR * 1.35, 1.8 + k, HEXR * 1.2)));
      if (k > 0.5) fk.add(cyl, '#3a1418', place(p.x + 0.2, 1.4, p.z + HEXR * 0.9, k * 3, new THREE.Vector3(0.05, 1.6, 0.05), 0.3, 0.5));
    }
    this.fleshMat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.42, metalness: 0.05, emissive: '#2a0a12', emissiveIntensity: 0.3 }));
    this.flesh = fk.build(this.fleshMat, 'raices-flesh');
    if (this.flesh) {
      this.geos.push(this.flesh.geometry);
      this.group.add(this.flesh);
    }
    const floorSet = new Set(cells.map((h) => `${h.q},${h.r}`));
    this.walls(kit, veins, L.rocks.filter((h) => this.hexPt(h.q, h.r).z >= -5.0 * S * 0.866), floorSet, 1.8);
    // The door of roots at the foot: an arch of roots, the colliers' lanterns.
    const door = this.pt(L.door.x, L.door.y);
    for (let i = 0; i < 7; i++) {
      const a = Math.PI * (i / 6);
      const p0 = door.clone().add(new THREE.Vector3(Math.cos(a) * 2.4, 0, 1.6));
      const p1 = door.clone().add(new THREE.Vector3(Math.cos(a) * 1.4, 3.2 * Math.sin(a) + 0.6, 1.9));
      kit.add(cyl, BARK[i % BARK.length] as string, along(p0, p1, 0.22));
    }
    for (const c of L.colliers) {
      const p = this.pt(c.x, c.y, 1.0);
      this.lanterns.push(p);
      this.light(p, '#ff8a3a', 1.8, 0.55, 1);
    }
    // The four roots: from the Carcoma's middle to their walls (instanced pieces, their colour by kind and cut).
    const mid = this.pt(L.mid.x, L.mid.y);
    this.wallAt = L.roots.map((r) => this.pt(r.wall.x, r.wall.y));
    const rg = this.track(new THREE.CylinderGeometry(1, 1.1, 1, 8));
    // (Lit partly by their own colour: under the chamber's amber the mud, the resin and the ice all read one brown.)
    const rm = this.track(selfLit(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.5, metalness: 0.05, flatShading: true }), 0.38));
    this.croots = this.inst(rg, rm, 64, 'raices-carcoma-roots');
    this.croots.castShadow = true;
    const decoG = this.track(new THREE.IcosahedronGeometry(1, 0));
    this.cdeco = this.inst(decoG, this.track(selfLit(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.35, metalness: 0.05, flatShading: true }), 0.55)), 160, 'raices-carcoma-deco');
    // (The hidden one shows only where it goes in and comes out.)
    const hc = L.hidden.cells;
    L.roots.forEach((rt, k) => {
      if (rt.kind === 'hidden') {
        // Its points: the middle, its cells, the wall; a piece shows only between two points in sight.
        const pts = [mid, ...hc.map((h) => this.hexPt(h.q, h.r)), this.wallAt[k] as THREE.Vector3];
        const seen = (j: number): boolean => j === 0 || j === pts.length - 1 || (!!L.hidden.rot[j - 1] && L.hidden.vis[j - 1] === 1);
        for (let j = 1; j < pts.length; j++) {
          if (!seen(j - 1) || !seen(j)) continue;
          this.crootOf.push({ k, vis: true });
          this.crootSeg.push([pts[j - 1] as THREE.Vector3, pts[j] as THREE.Vector3]);
        }
        return;
      }
      const a = mid;
      const b = this.wallAt[k] as THREE.Vector3;
      for (let j = 0; j < 6; j++) {
        const pa = a.clone().lerp(b, j / 6);
        const pb = a.clone().lerp(b, (j + 1) / 6);
        const wob = Math.sin(j * 1.7 + k) * 0.35;
        pa.x += wob * 0.3;
        pb.x += Math.sin((j + 1) * 1.7 + k) * 0.35 * 0.3;
        this.crootOf.push({ k, vis: true });
        this.crootSeg.push([pa, pb]);
      }
      // Its look along it, from a little out of the Carcoma to the wall.
      const n = rt.kind === 'mud' ? 22 : rt.kind === 'resin' ? 18 : 26;
      for (let i = 0; i < n; i++) {
        const t = 0.14 + (i / n) * 0.84;
        const at = a.clone().lerp(b, t);
        const side = (hash(i, k * 7) - 0.5) * 0.7;
        const d = new THREE.Vector3().subVectors(b, a).normalize();
        const nx = -d.z;
        const nz = d.x;
        const m = new THREE.Matrix4();
        let c: THREE.Color;
        if (rt.kind === 'mud') {
          // Wet lumps of mud clinging to it, and drips off it to the earth.
          const r = 0.16 + hash(i, 3) * 0.14;
          m.compose(new THREE.Vector3(at.x + nx * side * 0.6, 0.32 + hash(i, 4) * 0.2, at.z + nz * side * 0.6), this.q4.setFromEuler(new THREE.Euler(i, i * 2, 0)), new THREE.Vector3(r, r * 0.7, r));
          c = new THREE.Color(i % 3 ? '#3a2616' : '#24160c');
        } else if (rt.kind === 'resin') {
          // Beads of amber resin welling out of it, bright.
          const r = 0.09 + hash(i, 5) * 0.08;
          m.compose(new THREE.Vector3(at.x + nx * side * 0.5, 0.4 + hash(i, 6) * 0.12, at.z + nz * side * 0.5), this.q4.identity(), new THREE.Vector3(r, r * 1.2, r));
          c = new THREE.Color(i % 2 ? '#ffb040' : '#e8801e');
        } else {
          // Shards of ice standing off it, blue and white.
          const h = 0.3 + hash(i, 7) * 0.4;
          m.compose(new THREE.Vector3(at.x + nx * side * 0.55, 0.3 + h * 0.4, at.z + nz * side * 0.55), this.q4.setFromEuler(new THREE.Euler(side * 0.8, i, -side * 0.6)), new THREE.Vector3(0.08, h, 0.08));
          c = new THREE.Color(i % 3 ? '#a8dcff' : '#eaf6ff');
        }
        this.cdecoOf.push({ k, m, c });
      }
    });
    // The resin's patches across its root.
    this.patchAt = L.patches.map((p) => this.pt(p.x, p.y));
    const dg = this.track(new THREE.CylinderGeometry(0.48, 0.54, 0.06, 12));
    const dm = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.3, metalness: 0.05, emissive: '#ffffff', emissiveIntensity: 0.0 }));
    this.patches = this.inst(dg, dm, L.patches.length, 'raices-resin');
    // The hidden root's mushrooms (black over the rot, white over sound) and the sound root's notched ends in sight.
    this.cellAt = hc.map((h) => this.hexPt(h.q, h.r));
    const stemG = this.track(new THREE.CylinderGeometry(0.08, 0.11, 1, 7).translate(0, 0.5, 0));
    const capG = this.track(new THREE.SphereGeometry(0.5, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55));
    let total = 0;
    hc.forEach((_h, i) => (total += (L.hidden.black[i] as number) + (L.hidden.white[i] as number)));
    this.stems = this.inst(stemG, this.track(new THREE.MeshStandardMaterial({ color: '#e6e2da', roughness: 0.7 })), Math.max(1, total), 'raices-stems');
    this.caps = this.inst(capG, this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.55 })), Math.max(1, total), 'raices-caps', 1);
    let q = 0;
    const stems = this.stems;
    const caps = this.caps;
    hc.forEach((_h, i) => {
      const nb = L.hidden.black[i] as number;
      const nw = L.hidden.white[i] as number;
      const p = this.cellAt[i] as THREE.Vector3;
      for (let k = 0; k < nb + nw; k++) {
        const a = nb + nw === 1 ? 0 : (k / (nb + nw)) * Math.PI * 2 + 0.5;
        const d = nb + nw === 1 ? 0 : 0.28;
        const x = p.x + Math.cos(a) * d;
        const z = p.z + Math.sin(a) * d;
        const tall = 0.62 + ((i * 7 + k * 3) % 4) * 0.05;
        this.m4.compose(this.v3.set(x, 0, z), this.q4.identity(), this.s3.set(1, tall, 1));
        stems.setMatrixAt(q, this.m4);
        stems.setColorAt(q, WHITE);
        this.m4.compose(this.v3.set(x, tall, z), this.q4.identity(), this.s3.set(0.52, 0.38, 0.52));
        caps.setMatrixAt(q, this.m4);
        caps.setColorAt(q, k < nb ? BLACK : WHITE);
        this.capOf.push(i);
        q++;
      }
      // The sound root in sight at its ends: pale, Olmo's notch on it.
      if (L.hidden.vis[i] === 2) {
        kit.add(cyl, '#8a7448', along(p.clone().add(new THREE.Vector3(-0.45, 0.14, -0.2)), p.clone().add(new THREE.Vector3(0.45, 0.14, 0.2)), 0.16));
        kit.add(new THREE.BoxGeometry(0.1, 0.06, 0.24), '#e6d8b8', place(p.x, 0.3, p.z, 0.4));
      }
    });
    this.finish(this.stems, q);
    this.finish(this.caps, q);
    // The frost root's crown off its spring in the wall: its grooves, buds and knots.
    const F = L.frost;
    this.fnodeAt = F.nodes.map((n) => this.pt(n.x, n.y));
    this.fknotAt = F.knots.map((k) => this.pt(k.x, k.y));
    const spring = this.pt(F.spring.x, F.spring.y);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const groove = (a: THREE.Vector3, b: THREE.Vector3): void => {
      const d = new THREE.Vector3().subVectors(b, a);
      const m = a.clone().add(b).multiplyScalar(0.5);
      const yaw = Math.atan2(d.x, d.z);
      kit.add(box, '#0c0a10', place(m.x, 0.01, m.z, yaw, new THREE.Vector3(0.5, 0.03, d.length())));
      veins.add(box, '#ff9a30', place(m.x, 0.02, m.z, yaw, new THREE.Vector3(0.08, 0.02, d.length() * 0.96)));
    };
    groove(spring, this.fnodeAt[0] as THREE.Vector3);
    F.nodes.forEach((n, i) => n.parent >= 0 && groove(this.fnodeAt[n.parent] as THREE.Vector3, this.fnodeAt[i] as THREE.Vector3));
    kit.add(new THREE.SphereGeometry(0.7, 10, 6), '#2a1a10', place(spring.x, 0.3, spring.z, 0, new THREE.Vector3(1, 0.6, 1)));
    this.light(spring.clone().setY(0.6), '#ffae3c', 2.2, 0.5, 0.4);
    // Each bud's notches on a dark slab beside it.
    F.nodes.forEach((n, i) => {
      if (!n.need) return;
      const p = this.fnodeAt[i] as THREE.Vector3;
      const sx = p.x + 0.95;
      const sz = p.z - 0.35;
      const wd = 0.3 + n.need * 0.13;
      kit.add(box, '#0a0605', place(sx, 0.45, sz - 0.03, 0, new THREE.Vector3(wd + 0.14, 0.78, 0.08), -0.32));
      kit.add(box, '#2a1a12', place(sx, 0.45, sz, 0, new THREE.Vector3(wd, 0.64, 0.1), -0.32));
      for (let k = 0; k < n.need; k++) kit.add(box, '#e6eef8', place(sx - (wd - 0.3) / 2 + k * 0.13, 0.47, sz + 0.06, 0, new THREE.Vector3(0.07, 0.42, 0.06), -0.32));
    });
    F.nodes.forEach((n, i) => {
      if (n.need || n.kids.length) return;
      const p = this.fnodeAt[i] as THREE.Vector3;
      kit.add(new THREE.CircleGeometry(0.42, 12).rotateX(-Math.PI / 2), '#080404', place(p.x, 0.02, p.z));
    });
    const tg = this.track(new THREE.TorusGeometry(0.36, 0.12, 6, 14));
    const tm = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.6, flatShading: true }));
    this.bands = this.inst(tg, tm, F.knots.length + 2, 'raices-bands');
    const bg = this.track(new THREE.SphereGeometry(0.15, 8, 6));
    this.beads = this.inst(bg, this.track(new THREE.MeshBasicMaterial({ color: '#ffffff' })), 30, 'raices-beads', 3);
    const budG = this.track(new THREE.SphereGeometry(0.34, 10, 7));
    this.buds = this.inst(budG, this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.4, flatShading: true, emissive: '#1a2a3a', emissiveIntensity: 0.5 })), 4, 'raices-buds');
    // The mud's net.
    this.buildNets(kit, [L.mud.nodes.map((h) => this.hexPt(h.q, h.r))]);
    // Elvira's three knots of sound root by the way, and the gates they shut (upright roots across the way).
    this.gateAt = L.gates.map((g) => this.pt(g.at.x, g.at.y));
    for (const p of this.gateAt) {
      kit.add(new THREE.SphereGeometry(0.42, 10, 7), '#8a7a48', place(p.x, 0.3, p.z, 0, new THREE.Vector3(1, 0.8, 1)));
      kit.add(new THREE.TorusGeometry(0.42, 0.08, 5, 14).rotateX(Math.PI / 2), '#c8b884', place(p.x, 0.32, p.z));
    }
    const barG = this.track(new THREE.CylinderGeometry(0.14, 0.22, 1, 7).translate(0, 0.5, 0));
    this.bars = this.inst(barG, this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.7, emissive: '#2a4a10', emissiveIntensity: 0.4, flatShading: true })), 24, 'raices-bars');
    // The trail of rot, the splinters' cone, the rot from the walls.
    const trG = this.track(new THREE.CircleGeometry(0.6, 12).rotateX(-Math.PI / 2));
    this.trail = this.inst(trG, this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.8, depthWrite: false })), 18, 'raices-trail', 2);
    const half = Math.atan((DRUID_CONE_HALF_BP + 0) / 10000);
    const fan = this.track(new THREE.CircleGeometry(1, 20, Math.PI / 2 - half, half * 2).rotateX(-Math.PI / 2));
    this.cone = new THREE.Mesh(fan, this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0.32, depthWrite: false, side: THREE.DoubleSide })));
    this.cone.renderOrder = 4;
    this.cone.visible = false;
    this.group.add(this.cone);
    const ringG = this.track(new THREE.RingGeometry(1, 4, 48).rotateX(-Math.PI / 2));
    this.rotRing = new THREE.Mesh(ringG, this.track(new THREE.MeshBasicMaterial({ color: '#160a14', transparent: true, opacity: 0.6, depthWrite: false })));
    this.rotRing.renderOrder = 2;
    this.rotRing.visible = false;
    this.group.add(this.rotRing);
    // Olmo's last face on the flesh wall, in a boss of old wood: eyes, no mouth (its mouth carved in the close).
    // (High on a tall boss of wood, so it shows over the heads of the people standing before it.)
    // (Where the close's camera looks: north-west of the middle, against the flesh.)
    const fp = this.pt(FACE_X, FACE_Y, 2.15);
    kit.add(new THREE.CylinderGeometry(1.2, 1.5, 3.6, 12), '#4a3020', place(fp.x, 1.8, fp.z - 0.35, 0, new THREE.Vector3(1.15, 1, 0.6)));
    for (let i = 0; i < 5; i++) kit.add(new THREE.BoxGeometry(0.06, 4.2, 0.06), '#2a1a0e', place(fp.x - 1.0 + i * 0.5, 2.2, fp.z + 0.36, 0));
    this.mouthCut = this.carvedFace(kit, fp.clone().setZ(fp.z + 0.14), 0, 1.5, false);
    this.face = this.mouthCut;
    if (face) this.light(fp.clone().setZ(fp.z + 0.8), '#ffd8a0', 2.8, 0.4);
    // The Carcoma's breath in the middle: a sickly pale glow over the black earth (not in the scenes at the mouth).
    if (!mouth) this.light(mid.clone().setY(0.3), '#9a88b0', 3.0, 0.14, 0.4);
    sph.dispose();
    cyl.dispose();
    box.dispose();
    this.drawChamberRoots(null);
  }

  private crootSeg: [THREE.Vector3, THREE.Vector3][] = [];

  /** The four roots' pieces: by kind, alive (thick, their colour) or cut (dry, grey, thin). */
  private drawChamberRoots(s: BattleState | null): void {
    const m = this.croots;
    if (!m) return;
    const w = s?.senda?.deep;
    const cut = w?.cut ?? [0, 0, 0, 0];
    const key = cut.join('');
    if (key === this.crootKey) return;
    this.crootKey = key;
    // Mud wet and dark brown, resin a bright amber, the hidden black-violet, the frost ice blue: each its own colour
    // (lit partly from within). Cut, a root goes grey, dry, thin and flat on the earth, and the piece by the Carcoma is
    // gone: the gap shows which ones she no longer feeds through.
    const COL = ['#4a2c1a', '#d07a22', '#3a2232', '#9ad8ff'];
    const R = [0.46, 0.34, 0.3, 0.38];
    let first = -1;
    let lastK = -1;
    this.crootSeg.forEach(([a, b], i) => {
      const k = (this.crootOf[i] as { k: number }).k;
      if (k !== lastK) {
        first = i;
        lastK = k;
      }
      const dead = !!cut[k];
      const d = this.v4.subVectors(b, a);
      const len = Math.max(1e-4, d.length());
      const t = i - first;
      const r = (R[k] as number) * (1 - Math.min(5, t) * 0.08) * (dead ? 0.5 : 1);
      this.q4.setFromUnitVectors(this.s3.set(0, 1, 0), d.divideScalar(len));
      const gone = dead && t === 0;
      this.m4.compose(this.v3.copy(a).add(b).multiplyScalar(0.5).setY(dead ? r * 0.3 : r * 0.6), this.q4, gone ? this.s3.set(0, 0, 0) : this.s3.set(r, len * (dead ? 0.94 : 1.04), r * 0.85));
      m.setMatrixAt(i, this.m4);
      m.setColorAt(i, dead ? DRY : this.c3.set(COL[k] as string));
    });
    this.finish(m, this.crootSeg.length);
    const dm = this.cdeco;
    if (dm) {
      let n = 0;
      for (const x of this.cdecoOf) {
        if (cut[x.k]) continue;
        dm.setMatrixAt(n, x.m);
        dm.setColorAt(n++, x.c);
      }
      this.finish(dm, n);
    }
  }

  private drawChamber(s: BattleState): void {
    const L = this.Cl;
    const C = s.rules?.senda?.deep?.carcoma;
    const w = s.senda?.deep;
    if (!L || !C || !w || !w.cut.length) return;
    this.drawChamberRoots(s);
    // The resin's patches: amber as they are, alight (orange, lit), burnt black.
    if (this.patches) {
      L.patches.forEach((_p, i) => {
        const p = this.patchAt[i] as THREE.Vector3;
        const v = w.resin[i] ?? 0;
        this.m4.compose(this.v3.set(p.x, 0.04, p.z), this.q4.identity(), this.s3.set(1, 1, 1));
        this.patches?.setMatrixAt(i, this.m4);
        this.patches?.setColorAt(i, i === C.rootAt ? this.c3.set(w.cut[1] ? '#2a2420' : '#6a3a14') : v > 0 ? EMBER : v < 0 ? this.c3.set('#1a1412') : this.c3.set('#9a6424'));
      });
      this.finish(this.patches, L.patches.length);
    }
    // The hidden root's mushrooms: cut cells' caps knocked over (gone) and a green mark; all white once it is cut.
    if (this.caps) {
      this.capOf.forEach((c, i) => {
        const black = i < this.capOf.length && (L.hidden.black[c] as number) > 0 && this.capIsBlack(c, i);
        this.caps?.setColorAt(i, w.cut[2] ? WHITE : black ? BLACK : WHITE);
      });
      if (this.caps.instanceColor) this.caps.instanceColor.needsUpdate = true;
    }
    // The frost crown: its beads and bands (until it melts), its buds.
    if (!w.cut[3]) {
      const out = this.drawFlow(s, C.frost.nodes, C.frost.knots, this.fnodeAt, this.fknotAt, this.pt(C.frost.spring.x, C.frost.spring.y), w.closed, w.pulseNext, C.pulse, { b: 0, k: 0 });
      this.finish(this.beads, out.b);
      this.beadN = out.b;
      this.finish(this.bands, out.k);
    } else {
      this.finish(this.beads, 0);
      this.beadN = 0;
      this.finish(this.bands, 0);
    }
    if (this.buds) {
      let n = 0;
      C.frost.buds.forEach((b) => {
        const p = this.fnodeAt[b] as THREE.Vector3;
        const st = w.buds[b] ?? -1;
        const sc = w.cut[3] ? 1.1 : st === 0 ? 1.05 : 0.9;
        this.m4.compose(this.v3.set(p.x, 0.4, p.z), this.q4.identity(), this.s3.set(sc, sc, sc));
        this.buds?.setMatrixAt(n, this.m4);
        this.buds?.setColorAt(n++, w.cut[3] || st === 0 ? GREEN : st === 1 ? this.c3.set('#c87a34') : ICE);
      });
      this.finish(this.buds, n);
    }
    this.drawNets(s, () => !w.cut[0]);
    // The gate shut across the way: a row of upright sound roots rising.
    if (this.bars) {
      let n = 0;
      C.gates.forEach((g, i) => {
        const k = this.gateT[i] as number;
        if (k <= 0.01) return;
        for (let j = -2; j <= 2; j++) {
          const p = this.pt(g.bar.x + j * 480, g.bar.y);
          this.m4.compose(this.v3.set(p.x, 0, p.z), this.q4.setFromEuler(this.e3.set(0, j, j * 0.06)), this.s3.set(1, 2.1 * k, 1));
          this.bars?.setMatrixAt(n, this.m4);
          this.bars?.setColorAt(n++, this.c3.set('#9a8a4a'));
        }
      });
      this.finish(this.bars, n);
    }
    // The trail of rot.
    if (this.trail) {
      let n = 0;
      for (const p of w.trail) {
        if (n >= this.trail.instanceMatrix.count) break;
        const left = Math.max(0, Math.min(1, ((p[2] as number) - s.tick) / Math.max(1, C.trail.last)));
        const q = this.pt(p[0] as number, p[1] as number);
        this.m4.compose(this.v3.set(q.x, 0.03, q.z), this.q4.identity(), this.s3.set(0.6 + left * 0.6, 1, 0.6 + left * 0.6));
        this.trail.setMatrixAt(n, this.m4);
        this.trail.setColorAt(n++, this.c3.copy(ROT).lerp(VIOLET, 0.15 * left));
      }
      this.finish(this.trail, n);
    }
    // The splinters' cone: from her to where the hero stood, filling as it comes.
    const boss = s.enemies.find((e) => e.id === w.boss);
    if (this.cone) {
      this.cone.visible = w.cone >= 0 && !!boss;
      if (this.cone.visible && boss) {
        const b = this.pt(boss.x, boss.y, 0.05);
        const yaw = Math.atan2(w.cx - boss.x, w.cy - boss.y);
        const k = 1 - Math.max(0, Math.min(1, (w.cone - s.tick) / Math.max(1, C.cone.warn)));
        this.cone.position.copy(b);
        this.cone.rotation.set(0, yaw - Math.PI / 2, 0);
        const len = (C.cone.len / 1000) * S;
        this.cone.scale.set(len, 1, len);
        (this.cone.material as THREE.MeshBasicMaterial).opacity = 0.18 + 0.32 * k;
      }
    }
    // The rot from the walls: dark outside the safe circle.
    if (this.rotRing) {
      this.rotRing.visible = w.rotR > 0;
      if (w.rotR > 0) {
        const r = (w.rotR / 1000) * S;
        this.rotRing.position.copy(this.pt(C.mid.x, C.mid.y + 1700, 0.06));
        this.rotRing.scale.set(r, 1, r);
      }
    }
  }

  /** Whether cap `i` (on cell `c`) is one of that cell's black ones (its first `black` caps are). */
  private capIsBlack(c: number, i: number): boolean {
    const first = this.capOf.indexOf(c);
    return i - first < ((this.Cl?.hidden.black[c] as number) ?? 0);
  }

  /** The scene's own flags (world.setCinema): `breath`, the Grandsire healed (its amber turns green). */
  setCine(o: { face?: boolean; breath?: boolean }): void {
    this.breath = !!o.breath;
  }

  /* ---------- moments ---------- */

  /** A moment of the scenes: the colliers' lanterns coming down (`colliers`), the hands on the earth (`hands`), the face's mouth carved (`carve`). */
  cue(c: string): void {
    if (c === 'colliers') this.colliersT = 0;
    if (c === 'hands') {
      this.handsT = 0;
      this.ctx.shake(0.25);
      const p = this.pt(0, 6062, 0.2);
      this.ctx.vfx.burst(p, { n: 20, color: '#8ee060', speed: 0.8, up: 1.6, size: 0.1, life: 30 });
    }
    if (c === 'carve' && this.mouthCut) {
      this.carveT = 0;
      this.mouthCut.visible = true;
      const p = this.pt(FACE_X, FACE_Y + 200, 2.4);
      this.ctx.flare(p, '#ffd8a0', 4, 26);
      this.ctx.vfx.burst(p, { n: 30, color: '#d8b080', speed: 1.4, up: 0.8, size: 0.06, life: 20, gravity: 0.6 });
    }
  }

  /* ---------- the board ---------- */

  apply(terrain: TerrainBuild, hour: Hour = 'dusk'): void {
    // Its own floor everywhere: the board's tiles and the world beyond them hidden.
    this.terrain = terrain;
    terrain.mesh.visible = false;
    terrain.decor.visible = false;
    if (terrain.fog) terrain.fog.group.visible = !this.inside;
    terrain.ground = () => 0;
    terrain.heights.fill(0);
    if (!this.inside) {
      this.dome = new SkyDome();
      this.group.add(this.dome.mesh);
    }
    this.paint(hour);
  }

  paint(hour: Hour, weather?: string): void {
    this.hour = hour;
    const scene = this.ctx.scene;
    const { hemi, key, rim } = this.ctx;
    if (this.inside) {
      scene.background = new THREE.Color(IN.bg);
      if (scene.fog instanceof THREE.Fog) {
        scene.fog.color.set(IN.fog[0]);
        scene.fog.near = IN.fog[1];
        scene.fog.far = IN.fog[2];
      }
      if (hemi) {
        hemi.color.set(IN.hemi[0]);
        hemi.groundColor.set(IN.hemi[1]);
        hemi.intensity = IN.hemi[2];
      }
      if (key) {
        key.color.set(IN.key[0]);
        key.intensity = IN.key[1];
      }
      if (rim) {
        rim.color.set(IN.rim[0]);
        rim.intensity = IN.rim[1];
      }
      return;
    }
    const look = OUT[hour] ?? OUT.dusk;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(0.5, hour === 'dusk' ? 0.08 : 0.3, 0.6);
    this.terrain?.fog?.tint({ low: look.fog[0], mid: look.fog[0], far: look.bg, alpha: 1 });
    void weather;
  }

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.pt(e.x, e.y, 0.3);
      if (e.kind === 'pulse') this.pulseAt = this.now;
      else if (e.kind === 'knot') {
        const shut = e.npc === 'shut';
        this.ctx.flare(p.clone().setY(0.8), shut ? '#c8b89a' : '#ffc070', 2.2, 12);
        this.ctx.vfx.burst(p, { n: 14, color: shut ? '#2a1a10' : '#ffae3c', speed: 1.2, up: 1.4, size: 0.1, life: 14 });
      } else if (e.kind === 'mouthOpen') {
        this.ctx.shake(0.35);
        this.ctx.flare(p.clone().setY(1.2), '#fff0c8', 4.2, 24);
        this.ctx.vfx.burst(p, { n: 34, color: '#ffae3c', speed: 2.4, up: 2.6, size: 0.13, life: 22, gravity: 0.8 });
        this.ctx.vfx.burst(p, { n: 20, color: '#2e1e34', speed: 1.8, up: 1.4, size: 0.2, life: 20, smoke: true });
        this.ctx.vfx.ring(this.pt(e.x, e.y, 0.06), 0.3, 2.2, '#ffd080', 12, 0.9);
      } else if (e.kind === 'mouthCrack') {
        this.ctx.shake(0.3);
        this.ctx.flare(p.clone().setY(0.8), '#ff8a6e', 3, 16);
        this.ctx.vfx.burst(p, { n: 28, color: '#3a2440', speed: 2.4, up: 2.0, size: 0.18, life: 20, gravity: 1 });
        this.ctx.vfx.ring(this.pt(e.x, e.y, 0.06), 0.3, 2.0, '#ff4f3a', 10, 0.8);
      } else if (e.kind === 'mouthClean' || e.kind === 'galleryClean') {
        this.ctx.flare(p.clone().setY(1.4), '#d8f8a8', 5, 30);
        this.ctx.vfx.burst(p, { n: 40, color: '#9ee070', speed: 2.0, up: 2.6, size: 0.12, life: 26 });
      } else if (e.kind === 'sow') {
        this.ctx.vfx.burst(p, { n: 16, color: '#8ee060', speed: 1.0, up: 1.6, size: 0.1, life: 16 });
      } else if (e.kind === 'tangle') {
        this.ctx.flare(p.clone().setY(0.6), '#ff4f3a', 2.6, 18);
        this.ctx.vfx.burst(p, { n: 18, color: '#c4402a', speed: 1.4, up: 1.2, size: 0.1, life: 18 });
      } else if (e.kind === 'knotCut') {
        this.ctx.vfx.burst(p, { n: 22, color: '#d8c8a0', speed: 1.8, up: 1.8, size: 0.09, life: 16, gravity: 1 });
      } else if (e.kind === 'drip') {
        this.ctx.vfx.burst(p, { n: 24, color: '#2e1e34', speed: 1.8, up: 1.0, size: 0.16, life: 18, gravity: 1 });
      } else if (e.kind === 'dripWarn') {
        this.ctx.vfx.burst(p.clone().setY(3.2), { n: 8, color: '#4a3456', speed: 0.2, up: -0.6, size: 0.12, life: 30, gravity: 0.6 });
      } else if (e.kind === 'rootCut') {
        const k = Number(e.n ?? 0);
        this.cutFlash[k] = 1;
        this.ctx.shake(0.45);
        this.ctx.flare(p.clone().setY(1.2), '#fff0c8', 4.5, 26);
        this.ctx.vfx.burst(p, { n: 36, color: ['#5a4028', '#ffae3c', '#2a2024', '#bfe4ff'][k] as string, speed: 2.4, up: 2.4, size: 0.14, life: 22, gravity: 0.8 });
      } else if (e.kind === 'regrow') {
        this.ctx.shake(0.4);
        this.ctx.vfx.burst(p, { n: 30, color: '#141016', speed: 2.0, up: 1.6, size: 0.2, life: 22, smoke: true });
      } else if (e.kind === 'thread') {
        this.ctx.vfx.burst(p.clone().setY(1.6), { n: 14, color: '#141016', speed: 0.6, up: 0.4, size: 0.16, life: 30, smoke: true });
      } else if (e.kind === 'ignite') {
        this.ctx.flare(p.clone().setY(0.5), '#ff9a3a', 2.8, 16);
        this.ctx.vfx.burst(p, { n: 18, color: '#ff8a2a', speed: 1.4, up: 2.2, size: 0.12, life: 18, gravity: -0.2 });
      } else if (e.kind === 'resinBack') {
        this.ctx.vfx.burst(p, { n: 16, color: '#d08a2a', speed: 0.6, up: 0.6, size: 0.1, life: 18 });
      } else if (e.kind === 'cellCut') {
        const bad = e.npc === 'bad';
        this.ctx.flare(p.clone().setY(0.6), bad ? '#ff4f3a' : '#c8f0a0', 2.6, 14);
        this.ctx.vfx.burst(p, { n: 22, color: bad ? '#ff6a4a' : '#8ed060', speed: 1.8, up: 2.6, size: 0.13, life: 16 });
        if (bad) this.ctx.shake(0.3);
      } else if (e.kind === 'budCrack') {
        this.ctx.vfx.burst(p, { n: 18, color: '#ffae3c', speed: 1.8, up: 1.4, size: 0.1, life: 16, gravity: 1 });
      } else if (e.kind === 'gate') {
        this.ctx.flare(p.clone().setY(1), '#c8f0a0', 3.4, 20);
        this.ctx.vfx.burst(p, { n: 30, color: '#8a7a48', speed: 2.2, up: 2.4, size: 0.14, life: 20, gravity: 1 });
        this.ctx.shake(0.3);
      } else if (e.kind === 'cone') {
        this.ctx.vfx.burst(p, { n: 22, color: '#d8c8b0', speed: 2.2, up: 1.0, size: 0.08, life: 14, gravity: 1 });
      } else if (e.kind === 'bite' || e.kind === 'swipe') {
        this.ctx.shake(0.5);
        this.ctx.vfx.burst(p, { n: 28, color: e.kind === 'bite' ? '#2e1e34' : '#3a2a20', speed: 2.6, up: 2.0, size: 0.16, life: 18, gravity: 1 });
      } else if (e.kind === 'seedHit') {
        this.ctx.flare(p.clone().setY(1.2), '#ff4f3a', 3, 18);
      } else if (e.kind === 'branch') {
        this.ctx.flare(p.clone().setY(1.6), '#9ee070', 3.6, 22);
        this.ctx.vfx.burst(p, { n: 24, color: '#8ee060', speed: 1.6, up: 2.2, size: 0.12, life: 20 });
      } else if (e.kind === 'bare') {
        this.ctx.shake(0.4);
        this.ctx.flare(p.clone().setY(1.6), '#e8e0f0', 4, 24);
      } else if (e.kind === 'carcomaDown') {
        this.ctx.shake(0.7);
        this.ctx.flare(p.clone().setY(1.2), '#fff4e8', 6, 34);
        this.ctx.vfx.burst(p, { n: 60, color: '#c8c0cc', speed: 1.6, up: 1.4, size: 0.12, life: 40, smoke: true });
      } else if (e.kind === 'chop') {
        this.ctx.vfx.burst(p.clone().setY(0.7), { n: 5, color: '#c8ccd2', speed: 1.4, up: 0.8, size: 0.06, life: 8 });
      }
    }
  }

  draw(s: BattleState): void {
    this.lastState = s;
    if (this.Ml) this.drawGallery(s);
    if (this.Cl) this.drawChamber(s);
    this.drawRings(s);
  }

  private frac(): number {
    return Math.max(0, Math.min(1, (this.now - this.tickAt) * 12));
  }

  /** The rings: the drip (red, closing), the bite, the swipe at the seed, the black thread where a root grows back, Elvira's knots ready. */
  private drawRings(s: BattleState): void {
    const rings = this.rings;
    const w = s.senda?.deep;
    const r = s.rules?.senda?.deep;
    if (!rings || !w || !r) return;
    let q = 0;
    const ring = (p: THREE.Vector3, rad: number, color: THREE.Color): void => {
      if (q >= rings.instanceMatrix.count) return;
      this.m4.compose(this.v3.set(p.x, 0.06, p.z), this.q4.identity(), this.s3.set(rad, 1, rad));
      rings.setMatrixAt(q, this.m4);
      rings.setColorAt(q++, color);
    };
    const closing = (x: number, y: number, R: number, until: number, warn: number, a: THREE.Color, b: THREE.Color): void => {
      const k = Math.max(0, Math.min(1, (until - s.tick - this.frac()) / Math.max(1, warn)));
      const p = this.pt(x, y);
      ring(p, R, a);
      ring(p, R * (0.12 + 0.88 * k), b);
    };
    if (r.mouths) for (const d of w.drips) closing(d[0] as number, d[1] as number, (r.mouths.drip.r / 1000) * S, d[2] as number, r.mouths.drip.warn, WARN, WARN2);
    const C = r.carcoma;
    if (C) {
      if (w.bite >= 0) closing(w.bx, w.by, (C.bite.r / 1000) * S, w.bite, C.bite.warn, WARN, WARN2);
      if (w.swipe >= 0) closing(w.sx, w.sy, (C.swipe.r / 1000) * S, w.swipe, C.swipe.warn, WARN, WHITE);
      // Elvira's knots, ready while she is bare: a pale green ring.
      if (w.bare >= 0) C.gates.forEach((g, i) => !w.gateUsed[i] && ring(this.pt(g.at.x, g.at.y), 0.75 + 0.08 * Math.sin(this.now * 4 + i), GREEN));
    }
    rings.count = q;
    rings.instanceMatrix.needsUpdate = true;
    if (rings.instanceColor) rings.instanceColor.needsUpdate = true;
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    const s = this.lastState;
    if (s && s.tick !== this.lastTick) {
      this.lastTick = s.tick;
      this.tickAt = now;
    }
    this.dome?.frame(now, camera);
    if (camera) lookPoint(camera, this.look);
    // The Grandsire breathing: its trunk swelling and easing slowly; the cleft's amber with it (green, healed).
    if (this.trunk) {
      const b = 1 + 0.012 * Math.sin(now * 0.7);
      this.trunk.scale.set(b, 1, b);
    }
    if (this.breath && this.breathT < 0) this.breathT = 0;
    if (this.breathT >= 0) this.breathT += dt;
    // The colliers' smoke up the path.
    if (this.smokeAt.length && Math.floor(now * 3) !== Math.floor((now - dt) * 3)) {
      const p = this.smokeAt[Math.floor(now * 3) % this.smokeAt.length] as THREE.Vector3;
      this.ctx.vfx.burst(this.v3.set(p.x, 1.4, p.z), { n: 1, color: '#5b5f68', speed: 0.02, up: 0.25, size: 0.4, life: 40, gravity: -0.01, spread: 0.1, smoke: true });
    }
    // The flesh wall beating slowly.
    if (this.fleshMat) this.fleshMat.emissiveIntensity = 0.3 + 0.22 * Math.max(0, Math.sin(now * 1.3)) ** 3;
    // The redraw between ticks: the beads, the growing roots, the gate rising.
    if (s) {
      if (this.Ml) this.drawGallery(s);
      const w = s.senda?.deep;
      if (this.Cl && w) {
        for (let i = 0; i < 3; i++) this.gateT[i] = Math.max(0, Math.min(1, (this.gateT[i] as number) + (w.gate === i ? dt * 4 : -dt * 2)));
        this.drawChamber(s);
      }
    }
    for (let k = 0; k < 4; k++) this.cutFlash[k] = Math.max(0, (this.cutFlash[k] as number) - dt * 0.6);
    if (this.carveT >= 0) this.carveT += dt;
    if (this.handsT >= 0) this.handsT += dt;
    if (this.colliersT >= 0) this.colliersT += dt;
    // The glow: the place's lights, the sap's beads, the open mouths, the cut roots' flash, the knots ready, the resin alight.
    this.glow.begin();
    const green = this.breathT >= 0 ? Math.min(1, this.breathT * 0.25) : 0;
    for (const l of this.lights) {
      const f = l.flick ? 0.82 + 0.18 * Math.sin(now * (2 + l.seed) + l.seed) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, green > 0 ? this.c3.copy(l.c).lerp(GREEN, green) : l.c, l.size, l.a * f);
    }
    if (this.cleft) {
      const br = 0.7 + 0.3 * Math.sin(now * 0.7);
      this.glow.add(this.cleft.x, this.cleft.y, this.cleft.z + 0.2, green > 0 ? this.c3.copy(AMBER).lerp(GREEN, green) : AMBER, 3.4 + br, 0.55 * br);
    }
    for (let i = 0; i < this.beadN; i++) {
      const b = this.beadAt[i] as THREE.Vector3;
      this.glow.add(b.x, b.y + 0.05, b.z, AMBER, 0.9, 0.75);
    }
    const w = s?.senda?.deep;
    if (this.Ml && w) {
      this.Ml.mouths.forEach((_m, k) => {
        const p = this.mouthAt[k] as THREE.Vector3;
        if ((w.open[k] ?? -1) >= 0) this.glow.add(p.x, 0.6, p.z, (w.clean[k] ?? -1) >= 0 ? GREEN : AMBER, 2.2 + 0.3 * Math.sin(now * 3 + k), 0.6);
        else this.glow.add(p.x, 0.5, p.z, VIOLET, 1.4 + 0.3 * Math.sin(now * 2 + k), 0.35);
      });
      if (this.pulseAt >= 0 && now - this.pulseAt < 0.6 && this.Ml) {
        const f = this.pt(this.Ml.foot.x, this.Ml.foot.y);
        this.glow.add(f.x, 0.5, f.z, AMBER, 3, 0.7 * (1 - (now - this.pulseAt) / 0.6));
      }
    }
    if (this.Cl && w && w.cut.length) {
      this.wallAt.forEach((p, k) => {
        const f = this.cutFlash[k] as number;
        if (f > 0) this.glow.add(p.x * 0.6, 1.2, p.z * 0.6, WHITE, 3 + f * 4, f);
      });
      // What feeds her: beads of light running along each root still whole, from its wall into the Carcoma, in its
      // colour (none along a cut one).
      for (let i = 0; i < this.crootSeg.length; i++) {
        const k = (this.crootOf[i] as { k: number }).k;
        if (w.cut[k]) continue;
        const [a, b] = this.crootSeg[i] as [THREE.Vector3, THREE.Vector3];
        const f = (now * 0.9 + i * 0.37) % 1;
        this.v3.copy(b).lerp(a, f);
        this.glow.add(this.v3.x, 0.75, this.v3.z, FEED[k] as THREE.Color, 1.1, 0.7);
      }
      w.resin.forEach((v, i) => {
        const p = this.patchAt[i];
        if (p && v > 0) this.glow.add(p.x, 0.5, p.z, EMBER, 1.8 + 0.3 * Math.sin(now * 9 + i), 0.75);
      });
      // The resin's smoke thread: which way the air runs along its patches.
      if (!w.cut[1] && this.Cl && Math.floor(now * 4) !== Math.floor((now - dt) * 4)) {
        const a = this.patchAt[this.Cl.rootAt] as THREE.Vector3;
        const b = this.patchAt[this.Cl.rootAt + w.wind] as THREE.Vector3 | undefined;
        if (a && b) {
          const d = this.v4.subVectors(b, a).normalize();
          this.ctx.vfx.burst(this.v3.copy(a).setY(0.6), { n: 1, color: '#d0c8c0', speed: 0, up: 0.05, size: 0.18, life: 30, gravity: 0, spread: 0.02, smoke: true });
          this.ctx.vfx.burst(this.v3.copy(a).addScaledVector(d, 0.9).setY(0.7), { n: 1, color: '#d0c8c0', speed: 0, up: 0.05, size: 0.15, life: 26, gravity: 0, spread: 0.02, smoke: true });
          this.ctx.vfx.burst(this.v3.copy(a).addScaledVector(d, 1.8).setY(0.8), { n: 1, color: '#d0c8c0', speed: 0, up: 0.05, size: 0.12, life: 22, gravity: 0, spread: 0.02, smoke: true });
        }
      }
      // The thread on the wall where a root grows back.
      const C = s?.rules?.senda?.deep?.carcoma;
      if (C && w.grow >= 0 && s && s.tick >= w.growAt - C.regrow.thread) {
        const p = this.wallAt[w.grow] as THREE.Vector3;
        const k = 1 - Math.max(0, (w.growAt - s.tick) / Math.max(1, C.regrow.thread));
        for (let i = 0; i < 5; i++) this.glow.add(p.x * (1 - i * 0.04), 0.6 + i * 0.5, p.z * (1 - i * 0.04), WARN, 1.0 + k, 0.5 + 0.3 * Math.sin(now * 8 + i));
      }
      // The Carcoma's mouth glowing pale while she feeds; her roots' ends at the walls.
      if (w.gate >= 0 && C) {
        const g = C.gates[w.gate];
        if (g) this.glow.add(g.bar.x * K, 1, g.bar.y * K, GREEN, 3.2, 0.5);
      }
    }
    if (this.lanterns.length && this.colliersT >= 0 && this.colliersT < 6) for (const p of this.lanterns) this.glow.add(p.x, p.y + 0.4, p.z, EMBER, 2.4, 0.6 * Math.min(1, this.colliersT));
    if (this.carveT >= 0 && this.carveT < 4) this.glow.add(FACE_X * K, 2.6, FACE_Y * K + 0.9, this.c3.set('#ffd8a0'), 3, 0.6 * (1 - this.carveT / 4));
    if (this.handsT >= 0 && this.handsT < 5) {
      const k = Math.sin(Math.min(1, this.handsT / 5) * Math.PI);
      this.glow.add(0, 0.4, 6062 * K, GREEN, 4, 0.5 * k);
    }
    this.glow.end();
    void this.v4;
  }

  /** The marks' world points for the HUD's offer (the knots, stones, patches, cells, Elvira's knots). */
  markPoints(s: BattleState): THREE.Vector3[] {
    return (s.rules?.senda?.deep?.marks ?? []).map((m) => this.pt(m.x, m.y, 0.4));
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
    void this.terrain;
    void this.map;
    void this.hour;
  }
}

/** Olmo's last face on the chamber's wall (sim point). */
const FACE_X = -3300;
const FACE_Y = -6900;

/** The splinters' cone's half-width (bp of its length, the tan of its half-angle): the sim's own number. */
const DRUID_CONE_HALF_BP = 1100;

/**
 * The bark (act 1, a crypt's descent): the rooms re-dressed as galleries of living wood inside the Grandsire, its grain
 * along the walls, the floor of heartwood; the amber light through the bark (the room lights turned amber), sap
 * dripping glowing from the roof; and along the walls, every few cells, Espino's smooth cuts with a curved blade (a
 * pale fresh crescent cut into the dark wood). The crypt's own flames and runes are put out.
 *
 * Draw calls: the cuts (one merged mesh), the glows (one). No lights of its own.
 */
export class BarkCrypt {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(260);
  private drips: { p: THREE.Vector3; seed: number }[] = [];

  constructor(
    private ctx: OmenCtx,
    map: MapDef,
    crypt: { mesh: THREE.Mesh; runes: THREE.Group; flames: { mesh: THREE.Object3D; glow: THREE.Sprite }[]; decor: THREE.Group },
    rooms: THREE.PointLight[],
  ) {
    this.group.name = 'bark';
    crypt.runes.visible = false;
    for (const f of crypt.flames) {
      f.mesh.visible = false;
      f.glow.visible = false;
    }
    rooms.forEach((l) => {
      l.color.set(0xffb060);
      l.intensity *= 1.05;
    });
    // The living wood: the walls dark with a vertical grain, their tops lighter; the floor heartwood in rings.
    const geo = crypt.mesh.geometry;
    const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (col && pos) {
      const c = new THREE.Color();
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = pos.getZ(i);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const grain = 0.5 + 0.5 * Math.sin(x * 9.1 + Math.sin(z * 2.3) * 2);
        const ringsK = 0.5 + 0.5 * Math.sin(Math.hypot(x, z) * 4.2);
        if (lum < 0.04) c.set('#2a1a10');
        // (The wood a shade lighter than the gallery's: the crypt's own light is low, and a dark brown went black.)
        else if (y > 0.5) c.set(WOOD[Math.floor(grain * 4.99)] as string).multiplyScalar(1.6);
        else if (y > 0.05) c.set(WOOD[Math.floor(grain * 4.99)] as string).multiplyScalar(1.15 + lum * 0.6);
        else c.set(HEART[Math.floor(ringsK * 3.99)] as string).multiplyScalar(0.62 + lum * 0.35);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    const L = map.dungeon;
    if (!L) return;
    // Espino's cuts on the walls round the floor, and sap veins; drips from the roof between.
    const R = map.radius;
    const W = 2 * R + 1;
    const floor = new Set<number>();
    for (const h of L.floor) floor.add((h.r + R) * W + (h.q + R));
    const D: [number, number][] = [
      [1, 0],
      [1, -1],
      [0, -1],
      [-1, 0],
      [-1, 1],
      [0, 1],
    ];
    const kit = new Kit();
    const veins = new Kit();
    const blade = new THREE.TorusGeometry(0.2, 0.025, 4, 14, Math.PI * 0.75);
    const box = new THREE.BoxGeometry(1, 1, 1);
    let k = 0;
    let cuts = 0;
    for (const h of L.floor) {
      for (const [dq, dr] of D) {
        const id = (h.r + dr + R) * W + (h.q + dq + R);
        if (floor.has(id)) continue;
        k++;
        const a = hexToWorld(h.q, h.r);
        const b = hexToWorld(h.q + dq, h.r + dr);
        const mx = a.x + (b.x - a.x) * 0.47;
        const mz = a.y + (b.y - a.y) * 0.47;
        const yaw = Math.atan2(a.x - b.x, a.y - b.y);
        const p = this.ctx.at(mx, mz, 0);
        if (k % 5 === 0 && cuts < 70) {
          // A cut with a curved blade: a crescent of pale fresh wood, smooth, two of them side by side.
          cuts++;
          for (let j = 0; j < 2; j++) {
            const off = (j - 0.5) * 0.22;
            kit.add(blade, '#d8b484', place(p.x + Math.cos(yaw) * off, 0.75 + j * 0.12, p.z - Math.sin(yaw) * off, yaw, new THREE.Vector3(1, 1.3, 1), 0, 0.5 + j * 0.2));
          }
        } else if (k % 3 === 0) {
          veins.add(box, '#ffb050', place(p.x, 0.6, p.z, yaw, new THREE.Vector3(0.05, 0.9 + (k % 4) * 0.2, 0.04), 0, ((k % 5) - 2) * 0.12));
          if (k % 9 === 0 && this.drips.length < 60) this.drips.push({ p: new THREE.Vector3(p.x, 1.2, p.z), seed: k });
        }
      }
    }
    blade.dispose();
    box.dispose();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.7, metalness: 0.02, emissive: '#3a2410', emissiveIntensity: 0.4 });
    this.mats.push(mat);
    const m = kit.build(mat, 'bark-cuts');
    if (m) {
      this.geos.push(m.geometry);
      this.group.add(m);
    }
    const vm = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.5, emissive: '#ff9a30', emissiveIntensity: 0.9 });
    this.mats.push(vm);
    const v = veins.build(vm, 'bark-veins');
    if (v) {
      this.geos.push(v.geometry);
      this.group.add(v);
    }
    this.glow.mesh.frustumCulled = false;
    this.group.add(this.glow.mesh);
  }

  tick(s: BattleState): void {
    void s;
  }

  frame(now: number): void {
    const g = this.glow;
    g.begin();
    for (const d of this.drips) {
      // A drop of sap gathering, falling, gathering again.
      const t = (now * 0.35 + d.seed * 0.137) % 1;
      const y = t < 0.7 ? d.p.y : d.p.y - (t - 0.7) * 3.6;
      g.add(d.p.x, Math.max(0.05, y), d.p.z, AMBER, 0.32, 0.85);
      g.add(d.p.x, d.p.y + 0.2, d.p.z, AMBER2, 1.3, 0.28);
    }
    g.end();
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

export type { GroveLook } from './grove-trophy.ts';
